import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { AxiosError } from 'axios'

import { Box, Checkbox, CircularProgress, Divider, Link, Stack, Typography, alpha, useMediaQuery, useTheme } from '@mui/material'
import { TERMS_AND_CONDITIONS_VERSION } from '@repo/legal'
// VRG-595: `Input` and `Skeleton` are layer-1 (§47) and both are used exactly
// as-is rather than hand-rolled here. `Input` renders the CONFIRMED email on
// the identity step as a read-only field — see that call site for why it is not
// a second registered `TextField`. `Skeleton` is the invite-resolving state
// (§19: a skeleton at the field's own footprint, never a spinner on a blank).
import { Alert, Button, Input, Skeleton } from '@repo/ui'
import { CheckIcon, MailOpenIcon, XIcon } from 'lucide-react'

import { PlanIntervalToggle, planIntervalSavingsLabel } from 'components'
import { TextField } from 'components/input/fields/TextField'
import { SelectField } from 'components/input/fields/SelectField'
// The plan step renders the SAME comparison matrix the authenticated billing
// plan-change page does (VRG-383) — one component, one row set, one tier-key
// lookup, rather than the near-copy this page used to carry. Imported from that
// page's directory because that is where it lives; cross-page component imports
// are an established pattern here (BillingPlanCheckoutPage does the same with
// the tag-shop checkout rows). Promoting it to `components/display` is the
// tidier end state and is deliberately NOT done in this change, to keep it out
// of conflict with main's in-flight work on that file.
import {
  PlanComparisonMatrix,
  type PlanColumnAction,
  type PlanColumnPlan,
} from 'pages/settings/BillingPlanChangePage/PlanComparisonMatrix'
import { EnterpriseContactCard } from 'pages/settings/BillingPlanChangePage/EnterpriseContactCard'
import { resolvePlanTierKey } from 'constants/planComparison'
import { PlanMatrixSkeleton } from './PlanMatrixSkeleton'
import { SignupPaymentStep } from './SignupPaymentStep'

import { AuthLogo } from '../components/AuthLogo'
import { AuthPrimaryButton } from '../components/AuthPrimaryButton'
import { AuthInlineLink } from '../components/AuthInlineLink'
import { AuthBackButton } from '../components/AuthBackButton'
import { useAuthPageTransition, AUTH_TRANSITION_MS } from '../components/useAuthPageTransition'
import { PasswordStrengthMeter } from '../components/PasswordStrengthMeter'
// VRG-595: `brandRampStops` is the invite banner's gradient border — the SAME
// two stops the step numerals ramp between, read from the one module that owns
// them rather than a new gradient invented here (§28/§47).
import { brandRampGradient, brandRampInk, brandRampStops } from '../components/authFieldChrome'

import { ROUTES } from 'routes'
import {
  signupApi,
  readAlreadyPaidError,
  PendingSignupCompany,
  SignupPlan,
} from 'services/signupApi'
import { checkSubdomainAvailability } from 'services/subdomainApi'
import { getDomainSuffix, slugifySubdomain, validateSubdomain, centsToDollar } from 'utils'
import { stripVeraPlanPrefix } from 'utils/planName'
import { serverErrorMessage } from 'utils/serverErrorMessage'
import { PASSWORD_POLICY_HELP_TEXT, passwordMeetsPolicy, mapWorkosPasswordError } from 'constants/passwordPolicy'
import { SUPPORT_EMAIL, SUPPORT_MAILTO } from 'constants/support'
import { AUTH_TITLE_SX } from '../components/authType'
import { authGlassCardSx } from '../components/authGlassCard'
import { authPx, authScaleSx } from '../components/authScale'

import { PRODUCTION_VOLUME_OPTIONS } from './signupFormOptions'

/** The published Terms of Service (generated from packages/legal/terms-and-conditions.md). */
const TERMS_OF_SERVICE_URL = 'https://vera.inc/terms-and-conditions'

// VERA-1095 — self-serve signup, rebuilt onto a 4-STEP stepper:
//   Step 1 "Your details"    — identity (first/last name, email, password)
//   Step 2 "Company details" — company name, Vera subdomain, annual
//                              production volume — founder path only, SKIPPED
//                              for an invited admin whose company already
//                              exists (their identity resolves straight to
//                              Step "Choose your plan" instead)
//   Step 3 "Choose your plan" — columns, monthly/annual toggle, comparison
//   Step 4 "Payment"          — Stripe ELEMENTS, rendered in-page as our own form
//
// STEP 4's HISTORY, in two moves. It was originally a redirect to
// checkout.stripe.com. It then became Stripe's EMBEDDED Checkout, which kept
// the visitor on the page but is a Stripe-owned iframe whose form pane cannot
// be styled by us at all (`branding_settings` reaches the order-summary pane
// and nothing else — measured). That left a white form card floating on the
// dark page. It is now `Elements` + `PaymentElement`, which we DO style, via
// `appearance` derived from the same `fieldChrome` tokens steps 1-3's fields
// use (signupPaymentAppearance.ts).
//
// What did NOT change is what the server does: step 3's CTA still makes ONE
// call that reserves the founder's identity/subdomain and returns a secret,
// and deferred provisioning (VRG-289) is untouched — the company is still
// created only once payment succeeds, by the same /signup/verify handler. Only
// the key it is called with moved, from a Checkout session id to a
// PaymentIntent id.
//
// THE DOM-RECYLING GUARD (re-derived for the stepper — read this before
// touching step layout): this file previously had a real bug (VERA-1095
// postmortem) where swapping between differently-shaped step trees AT THE
// SAME STACK POSITION let React recycle an uncontrolled <input> DOM node
// across two DIFFERENT field names, leaking a typed email into the
// company-name box. The prior fix kept every card permanently mounted at a
// fixed vertical position and only toggled visibility via `Collapse`.
//
// A stepper's whole point is to show one step at a time, so "stay mounted,
// never conditionally swap" had to be re-derived rather than dropped. The
// mechanism here: every step panel renders inside a horizontal flex "track"
// (`stepKeys.map(...)`), and EVERY panel in that map gets a permanent,
// distinct, NEVER-REUSED `key` (`key={stepKey}`, one of the literal strings
// 'identity' | 'company' | 'plan'). React's keyed reconciliation matches
// subtrees by key, not by array position — so 'identity' and 'company' can
// never be confused for each other's DOM even though they sit side-by-side
// in the same flex row and even though the row's total LENGTH changes (3
// panels for a founder, 2 for an invited admin — see below). Which step is
// "current" is expressed purely as a CSS `transform: translateX(...)` on the
// track, not by mounting/unmounting a panel — so no field's DOM node is EVER
// destroyed and recreated at a position another field used to occupy. This
// is a stronger guarantee than the original Collapse approach, not a weaker
// one: Collapse only avoided the bug by accident (single Stack position,
// vertical show/hide); a keyed horizontal track can't produce it even in
// principle, because there is no shared position for two different fields to
// collide at.
//
// The ONE exception, carried over unchanged from the pre-stepper file:
// whether the visitor is a founder (Step 2 applies) or an invited admin (it
// doesn't) is resolved ONCE, from `check-eligibility`, before the user could
// ever have typed into Step 2 — so `stepKeys` changing length (4 -> 3) at
// that exact moment never loses input or reuses a DOM node; the 'company'
// key simply never gets a panel in the invited-admin case.
//
// Adding 'payment' costs nothing against that guard for the same reason it is
// safe generally: it is a NEW key, so it cannot collide with an existing
// panel's subtree, and it holds no form field of ours at all (its only child is
// a Stripe iframe, which owns its own inputs in another document).
type Phase = 'gate' | 'not-eligible' | 'confirm-join' | 'joined'
type EligibilityState = 'idle' | 'checking' | 'passed'
/*
  VRG-595 — 'email' is THE ELIGIBILITY GATE, and it is deliberately a panel in
  this track rather than a separate phase.

  The defect it fixes: `onContinueFromIdentity` validated first name, email AND
  password and only THEN called `check-eligibility`, so an uninvited visitor
  filled in three fields, chose a password, pressed Continue and was told they
  were never invited. Everything they typed was discarded by a refusal that
  could have been made from the email alone.

  WHY A PANEL AND NOT A NEW `Phase`. AC2 already made `stepIndex` DERIVED —
  `eligibilityState === 'passed' ? requestedStepIndex : 0` — precisely so the
  "nothing past the gate renders until eligibility passed" invariant holds by
  construction instead of by every call site remembering. Making the gate index
  0 of the SAME track EXTENDS that invariant rather than bypassing it: the
  formula is unchanged, and the thing it clamps to is now the gate itself. A
  parallel `Phase` would have put the gate outside the guard entirely and left
  the guard clamping to a step that no longer exists as an entry point.

  It also inherits the file-header DOM-recycling guarantee for free: 'email' is
  a NEW, never-reused key, so its input can never be recycled into another
  field's DOM node.
*/
type StepKey = 'email' | 'identity' | 'company' | 'plan' | 'payment'

/*
  ⚠️ 'email' IS NOT A STEP OF ACCOUNT CREATION and must never be numbered as
  one. The step indicator renders `stepKeys.slice(1)` (see `formStepKeys`), so
  "Your details" stays step 1 exactly as it was before the gate existed. This
  Record stays TOTAL over `StepKey` — a partial map with a `??` fallback is how
  a new step ends up silently unlabelled — but the 'email' entry is only ever
  read if someone puts the gate back into the numbered set, which is the moment
  they should come and read this comment.
*/
const STEP_LABEL: Record<StepKey, string> = {
  email: 'Eligibility',
  identity: 'Your details',
  company: 'Company details',
  plan: 'Choose your plan',
  payment: 'Payment',
}

// Card 1 + Card 2 (the form cards) stay at a readable form width even once
// the page widens for Card 3's plan columns — only the plan step actually
// needs the extra room. Each step's outer container width now animates
// STEP-BY-STEP (440 -> 640 -> 1120) rather than the old single binary jump,
// since each step has its own panel rather than sharing one scroll column.
const NARROW_WIDTH = 440
const FORM_WIDTH = 640
/**
 * The email gate's width: sign-in's column, exactly (2026-09-23). Spencer:
 * "Access Vera is too wide". It is one field, a title and a button — the same
 * shape as sign-in — and at FORM_WIDTH it sat in a 640px card while sign-in's
 * identical card was 396, so moving between the two pages visibly resized it.
 */
const GATE_WIDTH = 396
const WIDE_WIDTH = 1120

// VERA-1095 (item 1): the sign-in-family gradient CTA is the shared
// `AuthPrimaryButton` (extracted on its second hand-roll, working-conventions
// §36 — see that component's own doc comment for the full history). The
// shared design-system components this page already uses (SelectField,
// PlanColumns, PlanComparisonMatrix, …) are theme-aware and now follow the
// app's actual mode; only the PRIMARY CTA needs the auth family's own
// gradient button, which `AuthPrimaryButton` provides in one place instead
// of a page-local `GRADIENT_CTA_SX` const.

/**
 * Page title, matched to the sign-in page rather than to `variant="h1"`.
 *
 * Now `AUTH_TITLE_SX` (30px/700, from vera.inc's /enter), shared with sign-in.
 * h1 in this theme is 36px/700 with a 54px line-height, which next to sign-in's
 * then-24px/600 made the two pages read as different products — the whole point of the
 * redesign was that they should read as one. Stated as a constant so the phase
 * screens (joined / not-eligible / confirm-join) cannot drift from the step titles.
 */
const PAGE_TITLE_SX = AUTH_TITLE_SX

export const SignUpPage = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  // VERA-1095: cross-fade back to sign-in, the mirror of the sign-in page's
  // link out to here. Scoped to the "Already have an account? Sign in" link
  // only — the post-activation CTA below navigates after a state change the
  // user has already been shown, where a fade would read as lag, not polish.
  const { leaveTo: leaveToSignIn, exitSx: authExitSx } = useAuthPageTransition()
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

  // VERA-1095: the signup cards drop their outline in DARK only. There the
  // border is grey[100] (#27272a) on a #0f0f12 card — barely a line — while the
  // card already reads as a dark island on the lit purple field behind it. In
  // LIGHT the border is load-bearing: background.paper is #ffffff on a #f4f5f5
  // page, so without it a white card on a pale page has almost no edge.
  // Deliberately a call-site decision rather than a change to @repo/ui's
  // default, which would restyle ~100 cards to settle a question about two.

  // VERA-1095: this page used to force `darkTheme` on its whole subtree via a
  // local ThemeProvider, plus a MutationObserver that kept re-asserting
  // `document.documentElement.style.colorScheme = 'dark'`. Both are gone.
  //
  // The observer was working around a REAL bug, so it is worth recording why
  // removing it is a fix rather than a regression. `@repo/ui`'s `Alert` mounts
  // ABOVE any page-local ThemeProvider and cannot read it; it resolves its
  // mode by reading the document's CSS `color-scheme`, which `VeraThemeProvider`
  // owns. So a page that forced dark LOCALLY while the document still said
  // `light` put the two in disagreement, and every Alert here rendered with
  // light-mode text tokens on a black page — unreadable. A one-shot write on
  // mount could not fix it either: React fires child effects before parent
  // ones, so VeraThemeProvider's own write landed immediately afterwards and
  // overwrote it. Hence an observer, to keep winning a fight that kept
  // happening.
  //
  // The disagreement was the bug, and forcing a theme locally was what created
  // it. This page now renders under the app's real theme, so the page and the
  // document agree by construction and there is nothing left for an Alert to
  // resolve incorrectly. Deleting the cause beats out-shouting the symptom —
  // and it is what makes the page work in light mode at all.
  // VERA-1095: `?invite=<token>` from a signup-allowlist invite email — read
  // once so it survives re-renders. Resolved below to greet the invitee and
  // prefill their email (POST /signup/resolve-invite). It is NOT forwarded to
  // the subdomain-availability check — that endpoint's invite-token gate was
  // removed (it duplicated `by-subdomain`'s own enumeration exposure, which
  // can't be gated because the public certificate app depends on it, so
  // gating one and not the other bought nothing); it's rate-limited only now.
  const [searchParams] = useSearchParams()
  const inviteToken = useMemo(() => searchParams.get('invite') ?? undefined, [searchParams])

  // The invited-arrival experience: set only once `inviteToken` has round-
  // tripped through the server and come back `valid:true` — RESOLVE FIRST,
  // RENDER SECOND. A stranger who appends `?invite=x` to the URL gets a
  // failed resolve (or none at all, no token) and this stays null, so the
  // page renders exactly as it does today: no banner, no prefill, nothing
  // that reveals whether an invite system exists.
  const [inviteEmail, setInviteEmail] = useState<string | null>(null)
  /*
    VRG-595 — the invitee's first name AS THE INVITE REPORTED IT, held separately
    from the `firstName` FORM field the same value also prefills.

    Two different things that happen to start equal. The form field is the
    visitor's to correct; the banner is a greeting about the invite. Reading the
    banner off `watch('firstName')` would make the greeting re-address the reader
    on every keystroke while they edit their own name, which is a small horror.

    ⚠️ UNVERIFIED, SELF-REPORTED FREE TEXT from a public marketing form — anyone
    can type anything into it. Rendered as a React child (escaped by
    construction; no `dangerouslySetInnerHTML` anywhere on this path) and
    truncated at `INVITE_NAME_MAX` before it reaches the DOM.
  */
  const [inviteFirstName, setInviteFirstName] = useState<string | null>(null)
  /*
    Whether the `?invite=` token is still being resolved.

    Load-bearing for the gate, not cosmetic. An invited visitor must never see
    the eligibility gate (they have already been invited; asking them to prove
    it is friction that buys nothing), and the two requests that establish that
    — `resolve-invite` then `check-eligibility` — take a few hundred ms. Without
    this the gate's email field would paint and then be replaced, which is the
    exact screen the invite path is supposed to skip, just shown briefly.

    Starts `true` when and only when a token is present, so the non-invited path
    is completely unaffected and renders the gate on the first frame.
  */
  const [inviteResolving, setInviteResolving] = useState<boolean>(Boolean(inviteToken))
  /*
    True once the invite path has auto-advanced PAST the gate.

    Only consumer is the Back control: someone who never saw the gate must not
    be sent "back" to it, because from their point of view it is a screen they
    have never been on. With this set, Back on the identity step leaves for
    sign-in, exactly as it does for a non-invited visitor standing on the gate.
  */
  const [gateSkipped, setGateSkipped] = useState(false)
  const methods = useForm<{
    firstName: string
    lastName?: string
    email: string
    // VERA-1095 (Change 2): collected at signup, sent straight to WorkOS.
    password: string
    companyName: string
    subdomain?: string
  }>({
    /*
      VRG-595: every field starts as `''`, never `undefined`.

      This is PREVENTION, not the fix for the uncontrolled-to-controlled warning
      that prompted it — that was the email field spreading `value` in only after
      an invite resolved, and it is fixed at that call site. What this closes is
      the wider class: any field read back as `value={watch(name)}` is `undefined`
      on the first render when there is no default, so it mounts uncontrolled and
      flips the moment anything writes to it. The existing readers all guard with
      `|| ''` / `?? ''` (see `passwordValue`, `emailValue`, `subdomainValue`), so
      those guards are now belt-and-braces rather than load-bearing — but the next
      field added here gets a defined value without anyone having to remember.

      Safe to add: nothing on this form reads `isDirty`/`dirtyFields` or calls
      `reset()`, so moving the baseline from "empty" to "all fields empty string"
      changes no behaviour. `lastName` stays optional — the payload sends
      `getValues('lastName') || undefined`, so `''` still serialises as absent.
    */
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      companyName: '',
      subdomain: '',
    },
    // Errors appear on the CONTINUE attempt, not while typing. `onChange`
    // validated every registered field on any keystroke, so typing one letter
    // into First name lit up "Email is required" and "Password is required"
    // for fields the user had not reached yet.
    //
    // `reValidateMode: 'onChange'` is the other half: once a field HAS errored,
    // it re-checks as they fix it, so the message clears the moment it is
    // satisfied rather than persisting until the next submit.
    //
    // `reValidateMode` is dead code for this form and left here only as
    // documentation of the DEFAULT it can't reach — see `revalidateIfErrored`
    // below for what actually clears an error. React Hook Form only consults
    // `reValidateMode` once `formState.isSubmitted` is true, and that flag is
    // set exclusively by `handleSubmit()`. Step 1 and Step 2's Continue call
    // `trigger([...])` directly (deliberately — see `onContinueFromIdentity`'s
    // own comment on why the button stays enabled and validates on click), so
    // `isSubmitted` never becomes true until the FINAL step's checkout submit.
    // Confirmed by instrumenting a live run: an error set by `trigger()` sat on
    // screen, unchanged, for 600ms+ after the field was corrected via real
    // keystrokes — it only cleared on a SECOND explicit `trigger()` call (i.e.
    // pressing Continue again). That stale error text is real DOM height the
    // password-panel's ResizeObserver correctly measures, which is what showed
    // up as unexplained space above Continue once the checklist itself already
    // read all-green.
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })
  const { handleSubmit, getValues, setValue, trigger, unregister, watch, formState: { errors } } = methods
  // Live value for the strength meter. `watch` re-renders on change WITHOUT
  // validating, so it drives the meter without reintroducing the eager-error
  // problem that removing the per-keystroke `trigger()` calls just solved.
  const passwordValue = watch('password') || ''

  // Continue stays dead until the current step's required fields have SOMETHING in
  // them. Deliberately EMPTINESS, not validity — see `continueDisabled`.
  //
  // The distinction is the whole reason this is `watch` and not `trigger`. An
  // earlier version computed the same button state with per-keystroke
  // `trigger([...])` calls and had to be torn out, because `trigger()` VALIDATES
  // for real and WRITES errors: typing one letter into First name lit up "Email is
  // required" and "Password is required" for fields the visitor had not reached.
  // `mode: 'onSubmit'` cannot prevent that, since the mode only governs RHF's own
  // automatic validation and never an explicit trigger call.
  //
  // `watch` re-renders on change without validating, so the button can track the
  // fields without any error being written — the same reason the strength meter
  // above uses it. Errors still surface exactly where they did: on the Continue
  // click, from the `trigger()` calls in `onContinueFromIdentity` /
  // `onContinueFromCompany`. So an empty field disables the button, and a
  // MALFORMED one (a non-email, a too-short password) still lets you press it and
  // get told why. That split is intentional: "you have not filled this in" is
  // obvious enough to show silently, "this is wrong" needs words.
  const firstNameValue = watch('firstName') || ''
  const emailValue = watch('email') || ''
  const companyNameValue = watch('companyName') || ''

  // Mirrors the GATE step's trigger set — ['email'], its only field.
  const emailIncomplete = !emailValue.trim()

  // Mirrors `onContinueFromIdentity`'s own trigger set — ['firstName',
  // 'password']. Last name is absent from both because it is optional.
  //
  // VRG-595: `email` LEFT this set with the field itself. The identity step no
  // longer collects an email — the gate does, and identity shows the confirmed
  // address read-only — so gating Continue on it here would be gating on a
  // value this step has no control over and cannot show an error against.
  const identityIncomplete = !firstNameValue.trim() || !passwordValue.trim()

  // Re-validates a SINGLE field on change, but only once it already has an
  // error — this is the fix for the dead `reValidateMode` above. Scoped to
  // "already errored" so an untouched field is never eagerly validated (that
  // eager-validation is the exact bug `mode: 'onSubmit'` exists to prevent —
  // see this form's own `mode` comment); once a field HAS shown an error,
  // re-checking it on every keystroke is exactly what a person fixing that
  // one field expects, and is what the `reValidateMode` comment always
  // claimed already happened.
  const revalidateIfErrored = (name: 'firstName' | 'email' | 'password' | 'companyName' | 'subdomain') => {
    if (errors[name]) trigger(name)
  }

  // VERA-1095: resolve `?invite=<token>` once on mount. Deliberately silent on
  // failure (network error, invalid token, or no token at all) — an
  // unresolved invite is functionally identical to a visitor with no
  // `?invite=` param, so nothing here should surface as a page-level error.
  //
  /*
    VRG-595 — this effect now ALSO runs the eligibility check and skips the gate.

    WHY IT MUST, rather than letting the invite path reach the gate and press
    Continue like everyone else. `check-eligibility` is not only the allow/deny
    answer: it is the ONLY source of `pendingCompany`, the branch that routes an
    invited admin whose company already exists to ACTIVATION instead of
    CREATION. Send that visitor down the create path and
    `create-signup-company` refuses with a 409, which is the specific thing that
    branch exists to prevent. So moving eligibility off the identity step
    without running it somewhere on the invite path would not merely skip a
    screen, it would break the invited-admin flow outright.

    Running it here keeps ONE eligibility path for every visitor (`runEligibility`
    is shared with the gate's own Continue) and makes the skip a consequence of
    having genuinely passed, not a way around the check. `stepIndex` stays
    derived; an invited visitor whose allowlist entry was revoked between the
    invite email and the click still lands on `not-eligible`, correctly.

    Cost: two sequential public requests on arrival instead of one. Both are
    throttled at 10/15min per IP but on SEPARATE counters (different handlers),
    so this does not halve anyone's retry budget on either.
  */
  useEffect(() => {
    if (!inviteToken) return
    let cancelled = false
    signupApi
      .resolveInvite(inviteToken)
      .then(async (result) => {
        if (cancelled) return
        if (!result.valid || !result.email) {
          // An unusable token is treated as no token at all: fall through to the
          // ordinary gate rather than reporting anything. Clearing the resolving
          // flag is what reveals it.
          setInviteResolving(false)
          return
        }
        setInviteEmail(result.email)
        setValue('email', result.email)

        /*
          VRG-595 — prefill the name they already gave on the marketing site.

          ⚠️ PREFILL, NOT BOUND — the opposite treatment from the email above,
          and the difference is what each field IS. The email is the identity
          the invite was issued to and the server will only accept a signup for
          that exact address, so letting it be edited means filling in the whole
          form and being refused at the end; it is `readOnly`. A name is the
          visitor's own to correct — they may have typed it in a hurry on a
          marketing form weeks ago — and nothing downstream validates it, so
          binding it would be friction with no safety behind it.

          Absent is the ordinary case and deliberately does nothing: staff
          allowlist people directly (no waitlist row at all), and rows from
          before the site split the name have neither half. `??` rather than
          `||` is not load-bearing here since the API omits empty strings, but
          the guard is what keeps a `setValue('firstName', '')` from marking a
          pristine form dirty for no reason.
        */
        if (result.firstName) setValue('firstName', result.firstName)
        if (result.lastName) setValue('lastName', result.lastName)
        // The banner's greeting. `normaliseInviteName` is what turns "absent",
        // "empty string" and "whitespace only" into the SAME un-named state —
        // see its own comment for why that matters here specifically.
        setInviteFirstName(normaliseInviteName(result.firstName))

        // Skip the gate. `runEligibility` owns every branch (not-eligible,
        // confirm-join, pendingCompany, passed) so this call site does not
        // re-derive any of them.
        const outcome = await runEligibility(result.email)
        if (cancelled) return
        if (outcome === 'passed') setGateSkipped(true)
        setInviteResolving(false)
      })
      .catch(() => {
        // No-op on the RESOLVE — see the effect's own comment above. The gate is
        // revealed rather than left behind a permanent skeleton, which is the
        // one thing this handler must not do.
        if (!cancelled) setInviteResolving(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteToken])

  // The subdomain is suggested from the company name until the founder edits it
  // themselves — after that their choice stands, even if they rename the company.
  const [subdomainEdited, setSubdomainEdited] = useState(false)
  // Controlled value, read once here rather than re-calling `watch('subdomain')`
  // at each of its three use sites below (the field's `value`, the availability
  // effect's dependency, and the debounced check itself).
  const subdomainValue = watch('subdomain') ?? ''

  // Mirrors `onContinueFromCompany`'s trigger set — ['companyName', 'subdomain'].
  // Declared HERE, below `subdomainValue`, not up with `identityIncomplete`: that
  // variable already existed at this point in the file and a second `const` for it
  // is a redeclaration, while referencing it from above would hit the temporal dead
  // zone. `productionVolume` is deliberately NOT gated on — it is local state feeding
  // a soft plan preselection, not a required field, and gating on it would block a
  // visitor who does not want to disclose volume.
  const companyIncomplete = !companyNameValue.trim() || !subdomainValue.trim()

  // VERA-1095 (item 1): live subdomain availability, checked against the
  // existing public `GET /companies/public/subdomain/check/:subdomain`
  // (services/subdomainApi — the same probe InstallationPage's Settings editor
  // uses, there fired on blur; here debounced, since a signup visitor should
  // see the verdict while they're still typing rather than only after tabbing
  // away).
  const [subdomainAvailability, setSubdomainAvailability] = useState<
    { state: 'idle' | 'checking' } | { state: 'available' | 'unavailable'; subdomain: string }
  >({ state: 'idle' })
  // Bumped on every check ATTEMPT (i.e. every time the debounce timer actually
  // fires and a request goes out), and compared against on that request's
  // resolution. A plain debounce — cancelling the pending timer each keystroke
  // — already stops two requests from ever being IN FLIGHT during one
  // continuous fast-typing burst, since a superseded timer is cleared before it
  // can fire. It does NOT cover type -> pause (fires request A) -> type ->
  // pause (fires request B): both requests are real and in flight, and a slow
  // network can land A's response for the OLD value after B's response for the
  // CURRENT one. This ref is what discards a stale A landing after B.
  const subdomainCheckSeqRef = useRef(0)

  useEffect(() => {
    const trimmed = subdomainValue.trim().toLowerCase()
    // Skip anything the client-side format rule would already reject (blank,
    // too short, bad characters) — no reason to spend a request finding out
    // what `validateSubdomain` already knows for free, and it keeps the
    // suggested-but-not-yet-valid intermediate states of typing quiet.
    if (!trimmed || validateSubdomain(trimmed) !== true) {
      setSubdomainAvailability({ state: 'idle' })
      return
    }
    setSubdomainAvailability({ state: 'checking' })
    const checkId = ++subdomainCheckSeqRef.current
    const timer = setTimeout(() => {
      checkSubdomainAvailability(trimmed)
        .then((result) => {
          // Superseded by a later check — see the ref's comment above.
          if (subdomainCheckSeqRef.current !== checkId) return
          setSubdomainAvailability({
            state: result.available ? 'available' : 'unavailable',
            subdomain: trimmed,
          })
        })
        .catch(() => {
          if (subdomainCheckSeqRef.current !== checkId) return
          // A malformed subdomain 400s here, which the client-side pattern
          // rule already guards against reaching this call at all; anything
          // else is a network hiccup. Either way, don't assert a verdict this
          // request can't back up — let the visitor keep typing.
          setSubdomainAvailability({ state: 'idle' })
        })
    }, 400)
    // Cancels the PENDING timer on every keystroke — the debounce itself.
    // Only the value the visitor has settled on for 400ms ever reaches the
    // network.
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subdomainValue])

  // VERA-1095 (scope revision, mid-build): country, industry and employee
  // count were all dropped from Step 2. What's left is a single question —
  // annual production volume — which feeds `pickTierForVolume` below to
  // preselect a plan on Step 3. See signupFormOptions.ts for the buckets and
  // pickTierForVolume's own doc comment for the arithmetic. Managed as plain
  // state (not an RHF field) the same way `selectedPriceId` already is.
  const [productionVolume, setProductionVolume] = useState('')
  // True once the visitor has clicked a plan card directly — from then on the
  // volume-driven preselect effect below must never override their choice.
  const [planManuallySelected, setPlanManuallySelected] = useState(false)

  // Set when this email was invited to a company that already exists and is
  // awaiting payment. Its presence skips Step 2 entirely (nothing to create —
  // see the file-header comment on why that's safe here).
  const [pendingCompany, setPendingCompany] = useState<PendingSignupCompany | null>(null)
  /*
    Brand sign-up Terms acceptance (Spencer, 2026-10-09). The founding admin must tick
    "I accept the Terms of Service" on the plan step before the company account is
    created: POST /signup/company refuses the request (400, nothing written) unless
    `acceptedTerms` is literally true, and records it in `user_legal_acceptances`
    against TERMS_AND_CONDITIONS_VERSION, the same record the consumer app's
    create-account form writes (VERA-837). That record is what counts as the company
    accepting the Terms' publicity clause (website display, migration 0142).

    Founder path only. An invited admin (`pendingCompany`) pays for a company staff
    already created and does not see the box.
  */
  const [acceptedTerms, setAcceptedTerms] = useState(false)

  // VERA-1095 (VRG-289): the domain-matched company, shown in the "Join
  // <name>?" confirmation before /signup/join is ever called.
  const [matchedCompany, setMatchedCompany] = useState<{ id: string; name: string | null } | null>(
    null,
  )

  const [phase, setPhase] = useState<Phase>('gate')
  const [eligibilityState, setEligibilityState] = useState<EligibilityState>('idle')
  // The email check-eligibility last succeeded for. If the visitor edits the
  // email after Step 2/3 are revealed, that answer no longer applies to
  // whatever they're now typing — collapse back to the gate rather than let a
  // stale allow/company match silently carry over to a different address.
  const checkedEmailRef = useRef<string | null>(null)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Plan picker.
  const [plans, setPlans] = useState<SignupPlan[]>([])
  const [plansLoading, setPlansLoading] = useState(false)
  const [plansError, setPlansError] = useState(false)
  const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null)
  const [showAnnual, setShowAnnual] = useState(false)

  // VERA-1095: the PaymentIntent's client secret, minted by the plan step's
  // Continue and consumed by the payment step. Null until then, and null again
  // the moment the visitor goes back — see `goBack` for why holding a stale one
  // is worse than re-minting.
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  // The payment step's submit, handed up by SignupPaymentStep once Stripe has
  // loaded and `PaymentElement` is mounted. Held in STATE rather than a ref
  // because the footer CTA's disabled state depends on it: a ref would not
  // re-render when the form becomes ready, leaving the button dead.
  //
  // `setSubmitPayment(() => fn)` — the extra arrow is required, not style.
  // `useState`'s setter treats a bare function argument as an updater, so
  // passing `fn` directly would CALL it (submitting the payment) instead of
  // storing it.
  const [submitPayment, setSubmitPayment] = useState<(() => Promise<void>) | null>(null)
  const [paying, setPaying] = useState(false)
  /**
   * A DEFINITIVE payment failure — a declined card, not a network hiccup.
   *
   * Separate from `error` (the shared submit-error Alert) because the two want
   * different treatments. `error` is "that attempt didn't work, adjust and go
   * again" and sits above an intact form. This is "the payment was refused",
   * which needs to say so, say plainly that no money moved, and offer a route
   * that actually differs from pressing the same button again.
   */
  const [paymentFailure, setPaymentFailure] = useState<string | null>(null)
  const [reminting, setReminting] = useState(false)
  const registerPaymentSubmit = React.useCallback(
    (submit: (() => Promise<void>) | null) => setSubmitPayment(() => submit),
    [],
  )
  const onPaymentSucceeded = React.useCallback(
    (paymentIntentId: string) => {
      // FULL navigation, matching how the old Checkout return arrived.
      //
      // A client-side `navigate()` was tried here, to keep `LayoutAuthAmbient`
      // — and therefore the ambient blob field — mounted across the transition,
      // so the success screen could accelerate the very blobs the visitor had
      // been watching rather than a fresh set. It was REVERTED: driven in a
      // browser, the URL changed to /signup/success while `<Routes>` went on
      // rendering this page, leaving the visitor on a dead payment form after a
      // successful charge. Reproduced twice, from the `confirmPayment`
      // resolution both times; a `history.pushState` from the console at the
      // same moment re-rendered correctly, so it is specific to navigating out
      // of this promise (the app runs `<Routes future={{ v7_startTransition }}>`,
      // which defers the update). Not diagnosed further, because a hard load is
      // known-good here and phase-continuous blobs are not worth risking the
      // one screen that confirms someone's money arrived.
      //
      // Cost of the reload: the success page mounts a fresh blob field at phase
      // zero. It still converges — `setSurge(1)` acts on whatever field is
      // mounted — it simply is not the SAME field carried across.
      //
      // `payment_intent_client_secret` rides along because the success page
      // uses it to ask Stripe for the intent's real status, which is the only
      // way it can tell "declined" from "still settling". Stripe puts the same
      // parameter on its own 3DS return URL, so both routes into that page
      // carry the same two params.
      const query = new URLSearchParams({ payment_intent: paymentIntentId })
      if (clientSecret) query.set('payment_intent_client_secret', clientSecret)
      window.location.href = `${ROUTES.SIGN_UP_SUCCESS}?${query.toString()}`
    },
    [clientSecret],
  )

  /**
   * VERA-1095 (double-charge guard, client half): the API refused to mint
   * because this signup ALREADY HAS a payment that settled or is settling.
   *
   * The right answer is never an error message — it is to send them to the one
   * screen that can finish the job. `/signup/success` verifies the intent, so
   * the visitor who came back here after a failed confirmation is routed
   * straight into confirming the payment they already made, instead of being
   * shown a card form for a second one.
   *
   * A FULL navigation, matching `onPaymentSucceeded` — the success screen is
   * loaded the same way from every route into it, and the reasoning there (a
   * client-side `navigate()` left the visitor on a dead payment form after a
   * successful charge) applies identically.
   *
   * No `payment_intent_client_secret` rides along, unlike `onPaymentSucceeded`.
   * We do not have one: the API deliberately withholds the secret for a settled
   * intent, and the success page only uses that parameter to distinguish a
   * DECLINE from "still settling" — a distinction that is already answered here.
   *
   * The MISSING-ID branch is not defensive noise. Without an intent id there is
   * nowhere to route — /signup/success verifies BY id — so the only honest move
   * is to show the API's own sentence, which already says the payment is safe
   * and nothing more is owed. What it must never do is fall through: the
   * generic path runs `extractMessage`, which flattens `details` and would put
   * the bare word "succeeded" on screen as if it were an error.
   *
   * Returns true when it handled the error, so each call site reads as
   * "handled, or fall through to the normal error path".
   */
  const handleAlreadyPaid = React.useCallback((err: unknown): boolean => {
    const alreadyPaid = readAlreadyPaidError(err)
    if (!alreadyPaid) return false
    console.warn('[signup] payment already in flight — routing to verification', alreadyPaid)
    if (!alreadyPaid.paymentIntentId) {
      setError(alreadyPaid.message)
      return true
    }
    const query = new URLSearchParams({ payment_intent: alreadyPaid.paymentIntentId })
    window.location.href = `${ROUTES.SIGN_UP_SUCCESS}?${query.toString()}`
    return true
  }, [])

  /**
   * Re-mint a PaymentIntent for THIS signup, without leaving the payment step.
   *
   * WHY RE-MINT RATHER THAN RE-CONFIRM. A PaymentIntent is bound to the plan and
   * interval it was created with, and after some failures it is not reusable at
   * all. Confirming the same secret again is therefore not a retry so much as a
   * gamble on which of those two situations we are in. Minting is the path the
   * plan step already uses, so a retry that goes through it is retrying the same
   * way the first attempt happened.
   *
   * WHY THIS IS SAFE TO CALL REPEATEDLY. `create-signup-company.use-case`
   * is idempotent per email by construction: it finds the active pending-signup
   * row, reuses the WorkOS user and the Stripe customer, supersedes the previous
   * incomplete subscription, and updates the row rather than inserting a second
   * one. Retrying does not create a second user, a second company, or a second
   * subscription — and, because provisioning is deferred, still no company row
   * exists at this point either way.
   *
   * `getValues()` rather than `handleSubmit`: these values already passed
   * validation on the way to this step, and re-running validation here would
   * surface earlier-step errors on a step that cannot show them.
   */
  const remintPayment = React.useCallback(async () => {
    if (!selectedPriceId) return
    setReminting(true)
    setError(null)
    try {
      const email = getValues('email')
      if (pendingCompany) {
        const { clientSecret: secret } = await signupApi.activateCompany(email, selectedPriceId)
        setClientSecret(secret)
      } else {
        const { clientSecret: secret } = await signupApi.createCompany({
          email,
          firstName: getValues('firstName'),
          lastName: getValues('lastName') || undefined,
          password: getValues('password'),
          companyName: getValues('companyName'),
          subdomain: getValues('subdomain')?.trim().toLowerCase() || undefined,
          productionVolume: productionVolume || undefined,
          priceId: selectedPriceId,
          // Already ticked: the plan step's Continue is disabled until it is, and
          // this re-mint is only reachable after that step.
          acceptedTerms: true,
          termsVersion: TERMS_AND_CONDITIONS_VERSION,
        })
        setClientSecret(secret)
      }
      // Clearing this is what swaps the failure panel back for the card form.
      // It happens only after a new secret is in hand, so the form the visitor
      // returns to is never the dead one they just failed against.
      setPaymentFailure(null)
    } catch (err) {
      // The guard fires HERE most often of all: this is the retry button on the
      // payment-failure panel, and "the confirmation failed but the charge went
      // through" is exactly the state that panel can be reached in.
      if (handleAlreadyPaid(err)) return
      setError(extractMessage(err, 'We could not restart the payment. Please try again.'))
    } finally {
      setReminting(false)
    }
  }, [selectedPriceId, pendingCompany, productionVolume, getValues, handleAlreadyPaid])

  // Per-step live validity, used to disable Continue until that step's
  // required fields pass — trigger()'d from each relevant field's
  // `onValueChange` below rather than derived from RHF's global
  // `formState.isValid`, which would reflect ALL registered fields across
  // every step (Steps 1-3 stay permanently registered per the file-header
  // guard), not just the step currently showing.
  // NO eager per-keystroke revalidation. These used to be `revalidateIdentity` /
  // `revalidateCompany`, each calling `trigger([...])` on every `onValueChange` to
  // keep a boolean for the disabled Continue button.
  //
  // `trigger()` WRITES errors — it validates for real — so `mode: 'onSubmit'` could
  // not stop them: typing one letter into First name ran trigger over the whole step
  // and lit up "Email is required" and "Password is required" for fields the user had
  // not reached. The mode only governs RHF's OWN automatic validation, never an
  // explicit trigger call.
  //
  // Both existed solely to disable the button, so the whole trigger-based mechanism is
  // gone. `onContinueFromIdentity` / `onContinueFromCompany` still `trigger()` their own
  // field set on click, which is what surfaces the errors at the moment the user asks to
  // move on.
  //
  // The button is NOT "always enabled" any more — that was true for a while and this
  // comment used to say so. It is now disabled while the step's required fields are
  // EMPTY, computed from `watch` (see `identityIncomplete` / `companyIncomplete` above).
  // The reason that does not resurrect the bug this paragraph describes is precise and
  // worth keeping straight: the old mechanism asked "is this step VALID?", which only
  // `trigger()` can answer and which writes errors as a side effect. The new one asks
  // "is this field EMPTY?", which is a plain read of the value. Same button, different
  // question, and only one of them touches the error state.

  // Gates the plans fetch below (fires as soon as eligibility passes, i.e.
  // the moment Step 2 or Step 3 is first revealed — see that effect's own
  // comment on why this is deliberately earlier than "reached Step 3").
  const showPlanCard = eligibilityState === 'passed'

  // Step list — 4 panels for an invited admin (the company step doesn't apply
  // to them), 5 for a founder. See the file-header comment: this can only ever
  // change ONCE per session, before the company step could have been typed into.
  //
  // VRG-595: 'email' is index 0 on BOTH shapes — the eligibility gate precedes
  // every path, invited or not. It is not a step of account creation and is not
  // numbered as one; see `formStepKeys` below and the `StepKey` comment.
  const stepKeys = useMemo<StepKey[]>(
    () =>
      pendingCompany
        ? ['email', 'identity', 'plan', 'payment']
        : ['email', 'identity', 'company', 'plan', 'payment'],
    [pendingCompany],
  )
  /*
    VRG-595 (AC2) — THE STEP GATE IS DERIVED, NOT MAINTAINED.

    `requestedStepIndex` is where the flow WANTS to be; `stepIndex` is where it
    is ALLOWED to be. Nothing past the GATE renders unless `check-eligibility`
    has actually returned allowed for the email in the form.

    VRG-595 (second pass) — the formula below is UNCHANGED and the gate step was
    built to fit it rather than the other way round. Index 0 used to be
    `identity`, so the invariant read "an unchecked visitor sees the identity
    form and nothing beyond it" — which is how someone could fill in a name, an
    email and a password before being refused. Index 0 is now the eligibility
    gate itself, so the same one-line clamp reads "an unchecked visitor sees the
    gate and nothing beyond it", and the fillable form is unreachable rather than
    merely un-submittable. Extending what index 0 MEANS was the whole move; the
    guard did not need weakening or duplicating to accommodate it.

    Why derive it rather than keep trusting the call sites. The invariant held
    before this — `setStepIndex(1)` only ever ran after `allowed`, and
    `onEmailValueChange` collapsed back to 0 when the email moved off the
    checked one — but it held BY EVERY CALL SITE REMEMBERING. That is the shape
    of convention this repo has been burned by repeatedly (working-conventions
    §42's opt-in referrer prop, which 35 of 37 call sites forgot). A future
    `setStepIndex` written by someone who has not read `onContinueFromIdentity`
    would put a stranger on the company step, where they would fill in a
    company name and a subdomain and be refused by the server at submit — the
    exact "fillable form that fails at the end" this AC exists to prevent.
    Derived, that mistake renders nothing and is unreachable.

    ⚠️ This is a UI GUARD ONLY and is not what makes the flow safe. The real
    enforcement is server-side and unchanged: `CheckSignupEligibilityUseCase`
    runs again inside `create-signup-company`, `activate-signup-company` and
    `join-company-via-signup`. A guard in the browser decides what a person is
    ASKED to do, never what they are permitted to do.

    The BLOCKED-STATE decision (AC2 asked for one and for it to be recorded):
    an ineligible visitor gets `phase === 'not-eligible'` — the existing screen,
    extended rather than duplicated — not a redirect. A redirect throws away the
    email they just typed and lands them somewhere with no way to correct it;
    the blocked state names the reason generically and offers "Try a different
    email" in place. See the not-eligible panel below.
  */
  const [requestedStepIndex, setRequestedStepIndex] = useState(0)
  const stepIndex = eligibilityState === 'passed' ? requestedStepIndex : 0
  const setStepIndex = setRequestedStepIndex
  const currentStepKey = stepKeys[stepIndex] ?? 'email'

  /*
    The NUMBERED steps — account creation only, with the gate sliced off.

    Two different quantities that must not be conflated, the same distinction
    working-conventions §48 draws for a tour's outro step:

      stepKeys       what the ENGINE walks   (gate + the form steps)
      formStepKeys   what the USER is shown  (the form steps alone)

    Every number a visitor reads comes from the second. So "Your details" is
    still step 1 of 4 (or 1 of 3 for an invited admin), exactly as it was before
    the gate existed — the gate adds a screen, not a step. Numbering it would
    tell someone who has typed one email address that they are 20% of the way
    through creating an account, which is both wrong and discouraging.

    `formStepIndex` is -1 while the gate is showing, which is what keeps the
    indicator hidden there without a second condition.
  */
  const formStepKeys = useMemo(() => stepKeys.slice(1), [stepKeys])
  const formStepIndex = stepIndex - 1

  /*
    The lowest step this visitor can go back to.

    1 for someone the invite path auto-advanced past the gate — they have never
    seen it, so "Back" must leave for sign-in rather than reveal a screen they
    skipped. 0 for everyone else, whose first screen IS the gate.
  */
  const minStepIndex = gateSkipped ? 1 : 0

  // The step stack sizes to its tallest panel by default (CSS Grid, like the
  // flex row it replaced) — which made the shell as tall as the tallest step
  // no matter which step was showing (measured: Step 1's card 353px, viewport
  // 461px, so Continue sat 140px below the card it belongs to). The viewport's
  // height instead follows whichever panel is ACTIVE, animated so it grows and
  // shrinks as one movement together with the cross-fade — see the wrapper's
  // own `transition` comment for why height and opacity/transform now share
  // one duration and easing.
  //
  // `panelHeights` is a MEASURED height per panel (index-aligned with
  // `stepKeys`), not just the active one — every panel is permanently mounted
  // (file-header DOM-recycling guard), so every panel's height is knowable
  // from the moment it first mounts, long before it ever becomes active.
  // `activePanelHeight` is DERIVED from it (`panelHeights[stepIndex]`), which
  // is the load-bearing property here: switching `stepIndex` produces the
  // correct target height in the SAME render as the step change, with no
  // round-trip through an effect or an observer callback to wait on. Spencer's
  // report ("flashes rather than smooth grow") traced to exactly that kind of
  // round-trip — the opacity/transform cross-fade starts immediately (a
  // synchronous style change in the triggering render), while a height that
  // depends on an effect firing, a NEW ResizeObserver attaching to the new
  // active panel, and ITS OWN async initial callback landing, starts several
  // ms to a frame later — enough to desync the two into what reads as "the
  // box snaps, then the content fades" rather than one motion. A derived
  // value has no such lag: both properties change in the identical commit.
  const panelRefs = useRef<(HTMLDivElement | null)[]>([])
  const [panelHeights, setPanelHeights] = useState<(number | null)[]>([])
  const activePanelHeight = panelHeights[stepIndex] ?? null

  // ONE persistent ResizeObserver watching ALL panels for the component's
  // whole lifetime, not one recreated per step change — recreating it per
  // step was the earlier design, and it's what forced the "wait for the new
  // observer's guaranteed initial callback" round-trip above. Re-attaches
  // only if `stepKeys` itself changes (the founder/invited-admin panel-count
  // difference, resolved at most once per session — file-header comment).
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver((entries) => {
      setPanelHeights((prev) => {
        const next = [...prev]
        let changed = false
        for (const entry of entries) {
          const idx = panelRefs.current.indexOf(entry.target as HTMLDivElement)
          if (idx === -1) continue
          // `contentBoxSize`/`entry.contentRect` reports the CONTENT box
          // (excludes border/padding); the wrapper's height needs to match
          // the panel's full rendered box, so read the BORDER box.
          //
          // ⚠️ In the element's own layout px, NOT `getBoundingClientRect()`
          // (2026-10-09): that is VISUAL px after the app text zoom
          // (`#app { zoom }`, Cmd -), and writing it back as `height` applies
          // the zoom twice, clipping the bottom of the step on zoom-out. Same
          // defect and fix as SignInPage's step panels; see the note there.
          const h =
            entry.borderBoxSize?.[0]?.blockSize ?? (entry.target as HTMLElement).offsetHeight
          if (next[idx] !== h) {
            next[idx] = h
            changed = true
          }
        }
        return changed ? next : prev
      })
    })
    panelRefs.current.forEach((node) => {
      if (node) observer.observe(node)
    })
    return () => observer.disconnect()
  }, [stepKeys])

  const pickerPlans = plans.map(toPlanColumn)
  const filteredPlans = filterPlansByInterval(pickerPlans, showAnnual)
  // Derived from the BOTH-interval catalog (`pickerPlans`, not `filteredPlans`):
  // the claim compares the two intervals, so it needs a tier's monthly and
  // annual prices at once. `null` while plans are loading — no pill rather than
  // a guessed one. See `planIntervalSavings.ts` for why this is not a literal.
  const intervalSavingsLabel = planIntervalSavingsLabel(pickerPlans)
  const selectedPlan = pickerPlans.find((plan) => plan.id === selectedPriceId) ?? null

  // ---- Plan step: the three callbacks `PlanComparisonMatrix` renders a column
  // header from. The matrix owns the layout; the page owns what each column
  // SAYS, which here is a selection, not an upgrade path.

  /**
   * Signup's column CTA is a SELECT, not billing's Upgrade/Downgrade: nobody is
   * on a plan yet, so there is no direction to move in. Clicking sets
   * `selectedPriceId` (and pins it against the volume-driven preselect); the
   * page's own Continue button is what proceeds to Checkout.
   *
   * The selected column reads "Selected", filled and disabled — the same
   * treatment billing gives its current column, and paired with the matrix's
   * `currentPlanTitle` tint band it is the whole selected state. Disabling it is
   * not a dead end: the button's only job is to select, and it already is.
   */
  const planAction = (plan: PlanColumnPlan): PlanColumnAction => {
    const isSelected = plan.id === selectedPriceId
    return {
      label: isSelected ? 'Selected' : 'Select',
      variant: isSelected ? 'primary' : 'secondary',
      // NOT disabled. "Selected" was `disabled: true`, which renders at the
      // disabled treatment's low contrast — so the one column the step most
      // wants you to read was the least legible thing on it.
      //
      // Disabled and selected mean different things. Disabled says "you cannot
      // do this"; selected says "this is your choice". Borrowing the disabled
      // STYLE to express selection inherits a contrast reduction that exists to
      // signal unavailability, which is the opposite of the message.
      //
      // Leaving it live costs nothing: re-selecting the plan already selected
      // is idempotent. The state is carried by the label, the primary fill and
      // the column's tint band (§34 "chosen"), none of which need dimming.
      disabled: false,
      onClick: () => {
        setSelectedPriceId(plan.id)
        setPlanManuallySelected(true)
      },
    }
  }

  /**
   * Monthly <-> Annual, carrying the SELECTION across with it.
   *
   * The interval decides which SKUs are even on screen, so a selected monthly
   * price id is not in the annual list and vice versa. Without the remap the
   * step contradicts itself: the matrix highlights the selected column (it
   * matches on tier TITLE, which survives the switch) while every button reads
   * "Select" — and Continue would check out the price for the interval the
   * visitor just switched away from, which is the half that costs money.
   *
   * Matched on tier key, not title, for the same reason everything else here is
   * (VRG-383). `planManuallySelected` deliberately stays set: switching interval
   * is not un-choosing a tier, so the volume-driven preselect must not reclaim
   * it.
   */
  const handleIntervalChange = (annual: boolean) => {
    setShowAnnual(annual)
    setSelectedPriceId((previous) => {
      const current = pickerPlans.find((plan) => plan.id === previous)
      if (!current) return previous
      const counterpart = filterPlansByInterval(pickerPlans, annual).find(
        (plan) => resolvePlanTierKey(plan) === resolvePlanTierKey(current),
      )
      return counterpart?.id ?? previous
    })
  }

  /**
   * The saving the SELECTED plan would make by switching to annual — the inverse of
   * `planSavingsLabel` below, which only computes once you are already on annual.
   *
   * Null unless there is a real saving to name. A nudge that cannot state a number
   * ("prefer annual?" with nothing after it) is noise, and one that names a
   * non-saving would be worse than noise. Matched on tier key rather than title,
   * since title is Stripe-authored copy that can be renamed in the dashboard.
   */
  const annualSavingLabel = ((): string | null => {
    if (showAnnual || !selectedPlan) return null
    const annualCounterpart = filterPlansByInterval(pickerPlans, true).find(
      (plan) => resolvePlanTierKey(plan) === resolvePlanTierKey(selectedPlan),
    )
    if (!annualCounterpart) return null
    const saving = selectedPlan.amount * 12 - annualCounterpart.amount
    if (saving <= 0) return null
    return `Save ${centsToDollar(saving)}`
  })()

  /** Annual saving against paying monthly for the same tier, matched by title. */
  const planSavingsLabel = (plan: PlanColumnPlan): string | null => {
    if (!showAnnual) return null
    const monthlyCounterpart = pickerPlans.find(
      (candidate) =>
        candidate.title === plan.title && matchesPlanInterval(candidate, false),
    )
    if (!monthlyCounterpart) return null
    const saving = monthlyCounterpart.amount * 12 - plan.amount
    // `amount` is cents; plan prices are whole dollars, so no decimals.
    return saving > 0 ? `Save ${centsToDollar(saving)}` : null
  }

  /**
   * VERA-1095: the per-column "Pays for itself" line was REMOVED, not shortened
   * again. It rendered identically in all four columns, and a line that reads
   * the same everywhere carries no comparative information — in a table whose
   * entire job is to differentiate, it was four repetitions of one sentence
   * occupying the most valuable row on the page.
   *
   * The claim itself is still worth making; it just is not a per-column fact.
   * The honest home for it is the "Included activations / mo" row's own
   * tooltip, where it sits once, next to the numbers that vary. Not done here
   * because that row lives in the SHARED `constants/planComparison` and the
   * billing page reads it too.
   *
   * The matrix's `planSubline` slot is deliberately left in place — it is
   * generic, optional, and unused by both pages now.
   */

  /**
   * Enterprise "Contact sales" on the PUBLIC signup page.
   *
   * A new tab to the marketing site's sales form, NOT the in-app support intake
   * the billing page routes to. That difference is the whole point: the billing
   * page's caller is an authenticated customer whose company, plan and usage we
   * already hold, so making them retype it into a lead form is insulting. Here
   * the caller is a stranger mid-signup — a lead form is exactly right, and the
   * support intake is behind auth they do not have.
   *
   * New tab rather than a navigation, so a visitor who has already filled in
   * two steps does not lose them to a detour.
   */
  /**
   * Enterprise "Contact sales" leaves for the MARKETING site's contact page.
   *
   * Correct for this surface specifically: the visitor is unauthenticated and
   * mid-signup, so there is no in-app support channel to hand them and no
   * account to attach a request to. `_blank` so a half-finished form is not
   * discarded by the navigation.
   *
   * `www.` to match `AuthLogo`, which is the other outbound link on these pages
   * — the apex redirects, but two links to the same site written two ways is
   * the kind of drift that later becomes two different destinations.
   */
  const handleContactSales = () => {
    window.open('https://www.vera.inc/contact', '_blank', 'noopener,noreferrer')
  }

  // Fetch plans as soon as eligibility passes (i.e. the moment Step 2 or,
  // for an invited admin, Step 3 is revealed) rather than waiting for the
  // visitor to actually reach Step 3 — the volume-driven preselect below
  // needs the LIVE catalog (to resolve a bucket's tier to a real plan via
  // `tierKey`) to be ready by the time they answer Step 2's production-volume
  // question, not after.
  useEffect(() => {
    if (!showPlanCard || plans.length > 0) return
    let cancelled = false
    setPlansLoading(true)
    setPlansError(false)
    signupApi
      .listPlans()
      .then((data) => {
        if (cancelled) return
        setPlans(data)
        // Preselect the cheapest MONTHLY plan so the CTA is actionable
        // immediately — `prev ?? cheapest` preserves an invited admin's
        // pre-selected `intendedPriceId` (set in onContinueFromIdentity,
        // which runs before this effect's fetch resolves). The
        // production-volume effect below refines this further once/if the
        // visitor answers that question.
        const cheapestMonthly = filterPlansByInterval(data.map(toPlanColumn), false)[0]
        if (cheapestMonthly) {
          setSelectedPriceId((prev) => prev ?? cheapestMonthly.id)
        }
      })
      .catch(() => {
        if (!cancelled) setPlansError(true)
      })
      .finally(() => {
        if (!cancelled) setPlansLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPlanCard, plans.length])

  // VERA-1095 (scope revision) — soft plan preselect from annual production
  // volume. Never runs once the visitor has picked a plan card directly
  // (`planManuallySelected`); re-runs on interval toggle so the SAME tier
  // stays selected across Monthly/Annual. Reads `plans`/`showAnnual`
  // directly (not the derived `pickerPlans`/`filteredPlans`, which are new
  // array references every render) to keep this effect's dependency list
  // meaningful.
  useEffect(() => {
    if (planManuallySelected || !productionVolume || plans.length === 0) return
    const bucket = PRODUCTION_VOLUME_OPTIONS.find((option) => option.value === productionVolume)
    if (!bucket) return
    const tier = pickTierForVolume(plans.map(toPlanColumn), bucket.tier)
    if (!tier) return
    const match = filterPlansByInterval(plans.map(toPlanColumn), showAnnual).find(
      (plan) => resolvePlanTierKey(plan) === tier,
    )
    if (match) setSelectedPriceId(match.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productionVolume, plans, showAnnual, planManuallySelected])

  /**
   * The ONE funnel every `setError` on this page goes through.
   *
   * VRG-595: it now SANITISES what it relays. It used to return
   * `response.data.message` whenever one was present, which is how
   * "ThrottlerException: Too Many Requests" — a NestJS class name concatenated
   * with an HTTP status reason — reached a public signup screen verbatim, in an
   * error Alert, telling the reader nothing they could act on. Every path here
   * (eligibility, join, checkout, re-mint) shared that funnel, so all four could
   * relay framework noise; fixing it here rather than at the 429's own call site
   * is what makes that true of all of them at once.
   *
   * `serverErrorMessage` owns the judgement and the copy — see that module for
   * which shapes are refused and, more importantly, for the deliberate server
   * refusals ("This email is not eligible for self-signup.") that MUST still
   * pass through untouched. Both directions are asserted by
   * `test/server-error-message.guard.spec.ts`; an over-broad filter here would
   * silently replace a real refusal with generic copy, which is worse than the
   * leak it was written to stop.
   */
  const extractMessage = (err: unknown, fallback: string): string => {
    if (err instanceof AxiosError) {
      const data = err.response?.data as
        | { message?: string | string[]; details?: Record<string, string[]> }
        | undefined

      // Field errors first. A DTO rejection sets `message` to the bare string
      // "Validation errors" and puts the part worth reading — which field, and
      // why — in `details`, so reading `message` alone tells the user nothing
      // they can act on.
      const fieldMessages = Object.values(data?.details ?? {}).flat().filter(Boolean)
      const msg = data?.message
      const message = fieldMessages.length
        ? fieldMessages.join(' ')
        : Array.isArray(msg)
          ? msg.join(', ')
          : typeof msg === 'string'
            ? msg
            : undefined

      // The STATUS is passed, not just the message. A 429's body is unreadable
      // either way, but only the status knows that "please try again" is the
      // wrong advice — retrying immediately is exactly what fails while a rate
      // limit is open.
      return serverErrorMessage({ message, status: err.response?.status, fallback })
    }
    return fallback
  }

  /**
   * VRG-595 — THE ONE eligibility path, shared by the gate's Continue and the
   * invite auto-skip.
   *
   * This body is unchanged from the `onContinueFromIdentity` it was lifted out
   * of; only its callers moved. Extracting it is what makes the invite path a
   * SKIP OF A SCREEN rather than a skip of the check — the two entry points run
   * identical branching, so `pendingCompany`, the domain-match confirm and the
   * refusal cannot behave differently depending on how the visitor arrived.
   * Duplicating it into the effect was the alternative and is exactly the
   * second-hand-roll working-conventions §36 exists to stop: two copies of a
   * four-way branch that must agree forever.
   *
   * Returns the outcome so a caller can react to it. 'blocked' covers BOTH
   * terminal branches (not-eligible and confirm-join) because they are the same
   * thing from a caller's point of view: the phase changed, the stepper is no
   * longer on screen, do nothing further.
   *
   * ⚠️ This is the browser's copy of a decision the server makes again.
   * `CheckSignupEligibilityUseCase` runs inside `create-signup-company:162`,
   * `activate-signup-company:57` and `join-company-via-signup:58`, and nothing
   * here weakens any of them — moving the check EARLIER in the UI changes what
   * a person is asked to type, never what they are permitted to do.
   */
  const runEligibility = async (email: string): Promise<'passed' | 'blocked' | 'error'> => {
    setEligibilityState('checking')
    setError(null)
    try {
      const { allowed, matchedCompanyId, matchedCompanyName, pendingCompany: pending } =
        await signupApi.checkEligibility(email)

      if (!allowed) {
        setPhase('not-eligible')
        setEligibilityState('idle')
        return 'blocked'
      }

      if (matchedCompanyId) {
        // Employee path — show an explicit "Join <company>?" step; the write
        // only happens from onConfirmJoin below.
        setMatchedCompany({ id: matchedCompanyId, name: matchedCompanyName ?? null })
        setPhase('confirm-join')
        setEligibilityState('idle')
        return 'blocked'
      }

      checkedEmailRef.current = email

      if (pending) {
        // Invited-admin path — their company exists and is awaiting payment.
        // The company step doesn't apply; unregister its fields so a hidden
        // "required" company name can never block the final submit (see file
        // header).
        setPendingCompany(pending)
        unregister(['companyName', 'subdomain'])
        if (pending.intendedPriceId) setSelectedPriceId(pending.intendedPriceId)
      } else {
        setPendingCompany(null)
      }

      setEligibilityState('passed')
      // Index 1 is `identity` on both `stepKeys` shapes — the first step of
      // account creation, immediately after the gate.
      setStepIndex(1)
      return 'passed'
    } catch (err) {
      setEligibilityState('idle')
      // VRG-595: names OUR side. This is the gate's only failure copy, and the
      // reader has typed one email address — there is nothing they could have
      // got wrong, so an unattributed "something went wrong" invites them to
      // hunt for a mistake of their own that does not exist.
      setError(extractMessage(err, 'Something went wrong on our side. Please try again in a moment.'))
      return 'error'
    }
  }

  /**
   * The GATE's Continue — one field, then the eligibility check.
   *
   * This is the whole point of VRG-595's first half. It refuses on the ONE
   * thing a refusal can be decided from, before anyone has chosen a password or
   * typed their name, so a "you weren't invited" answer costs the visitor one
   * email address rather than a filled-in form.
   */
  const onContinueFromEmail = async () => {
    const valid = await trigger('email')
    if (!valid) return
    await runEligibility(getValues('email'))
  }

  // The identity step's Continue — client-side validation only. Eligibility ran
  // at the gate (or on the invite path, at mount), so there is no network
  // round-trip here any more and no branch to take: pass validation, advance.
  const onContinueFromIdentity = async () => {
    const valid = await trigger(['firstName', 'password'])
    if (!valid) return
    setStepIndex((index) => index + 1)
  }

  // Step 2's Continue — client-side validation only (subdomain UNIQUENESS is
  // checked server-side at final submit, same as before the stepper); no
  // network round-trip needed to advance.
  const onContinueFromCompany = async () => {
    const valid = await trigger(['companyName', 'subdomain'])
    if (!valid) return
    setStepIndex((index) => index + 1)
  }

  const goBack = () => {
    // VERA-1095: leaving the payment step DISCARDS its Checkout session.
    //
    // Keeping it would be the bug, not the optimisation it looks like. The
    // session is bound to the plan and interval selected when it was minted, so
    // a visitor who goes back specifically to change plan and returns would be
    // shown — and charged — the old one, with the matrix behind them insisting
    // otherwise. Re-minting on the way forward is cheap (an unpaid session
    // simply expires), and the founder path deliberately RESUMES its pending
    // row rather than duplicating anything, so a second Continue does not
    // create a second signup.
    //
    // It also keeps the iframe from living on in a hidden, permanently-mounted
    // panel: every panel here stays mounted by design (the DOM-recycling guard
    // at the top of this file), so without this the abandoned Checkout would
    // keep running off-screen for the rest of the session.
    // Leaving the payment step DISCARDS its PaymentIntent, and the submit
    // handle bound to it. Keeping either would be the bug, not the
    // optimisation it looks like: the intent is bound to the plan and interval
    // selected when it was minted, so a visitor who goes back specifically to
    // change plan and returns would be shown — and charged — the old one, with
    // the matrix behind them insisting otherwise. Re-minting on the way forward
    // is cheap (an unconfirmed subscription expires on its own, and the API
    // cancels the superseded one anyway), and the founder path deliberately
    // RESUMES its pending row rather than duplicating anything, so a second
    // Continue does not create a second signup.
    if (currentStepKey === 'payment') {
      setClientSecret(null)
      setSubmitPayment(null)
      setPaying(false)
      // A refusal belongs to the intent that was refused. Going back to change
      // plan mints a different one, so carrying the panel forward would report
      // a decline that no longer applies to what the visitor is now buying.
      setPaymentFailure(null)
    }
    // Clear the submit error on the way back.
    //
    // These errors are raised by a LATER step about an EARLIER step's field — the
    // one that prompted this is `Subdomain "x" is already taken by another company`,
    // which the server can only report at submit time, long after step 2 is behind
    // you. Going back is the visitor accepting the correction and heading to the
    // field to make it, so the message has done its job the moment they move.
    //
    // Left in place it becomes a liar: it names a subdomain the visitor is now
    // editing, sits above the very field being corrected, and does not re-evaluate
    // as they type, so a perfectly good new value still has "is already taken"
    // hanging over it. The next Continue re-runs the check and will re-raise it if
    // it is still true, so nothing is lost by clearing — the only thing dropped is
    // a claim that has gone stale.
    setError(null)
    // `minStepIndex`, not a literal 0 — an invited visitor who never saw the
    // gate must not be able to reverse into it. See that constant's comment.
    setStepIndex((index) => Math.max(minStepIndex, index - 1))
  }

  // VERA-1095 (VRG-289): the explicit confirmation this whole step exists
  // for. Only now does anything get written — a new user, a company_users
  // link, and a WorkOS invitation.
  const onConfirmJoin = async () => {
    setLoading(true)
    setError(null)
    try {
      await signupApi.joinCompany(getValues('email'), true)
      setPhase('joined')
    } catch (err) {
      setError(extractMessage(err, 'Something went wrong. Please try again.'))
    } finally {
      setLoading(false)
    }
  }

  /**
   * Moves the stepper onto the payment panel with a freshly-minted secret.
   *
   * The missing-secret branch is not defensive noise: it is the only thing
   * standing between an API that returned an unexpected shape (a rollback, a
   * proxy stripping the body) and a permanently empty payment step with no
   * error anywhere. Failing loudly on the plan step instead keeps the visitor
   * somewhere they can act.
   */
  const advanceToPayment = (secret: string | null) => {
    if (!secret) {
      setError('Could not start checkout. Please try again.')
      setLoading(false)
      return
    }
    setClientSecret(secret)
    setStepIndex((index) => index + 1)
    // Cleared on ARRIVAL. The CTA's loading pulse belongs to the plan step; the
    // payment step's own submit has its own in-flight state (`paying`).
    setLoading(false)
  }

  // Plan step's Continue: create the company (founder) or start payment for the
  // existing one (invited admin), then advance to the in-page payment step.
  //
  // What this call does server-side is unchanged by the move to Elements — same
  // WorkOS user, same subdomain reservation, same pending-signup row, same
  // deferred provisioning. Only what it returns changed: a PaymentIntent client
  // secret instead of a Checkout session's.
  //
  // The error handling is why the fetch lives here rather than inside the
  // payment panel: this ONE call can fail with "that subdomain is taken" or "an
  // account already exists for this email", which are answers to fields on
  // earlier steps. Surfacing them here, before advancing, keeps them next to the
  // CTA that caused them and leaves Back working. See SignupPaymentStep's doc.
  const onSubmitCheckout = handleSubmit(async ({
    email,
    firstName,
    lastName,
    password,
    companyName,
    subdomain,
  }) => {
    if (!selectedPriceId) return // submit is disabled without a plan; guard defensively
    // Same for the Terms box on the founder path: disabled until ticked, guarded anyway.
    if (!pendingCompany && !acceptedTerms) return
    setLoading(true)
    setError(null)
    try {
      if (pendingCompany) {
        const { clientSecret: secret } = await signupApi.activateCompany(email, selectedPriceId)
        advanceToPayment(secret)
        return
      }

      const { clientSecret: secret } = await signupApi.createCompany({
        email,
        firstName,
        lastName: lastName || undefined,
        password,
        companyName,
        // Blank means "generate one from the company name" — the API's own
        // slugify then owns it, rather than us sending an empty string that
        // would fail format validation.
        subdomain: subdomain?.trim().toLowerCase() || undefined,
        // VERA-1095: the Step 2 production-volume answer was collected into
        // `productionVolume` state (used client-side to soft-preselect a plan
        // tier) but never actually sent — the API now accepts and persists it
        // (`company_profile`), so it's wired through here rather than thrown
        // away. Blank means "skipped" (an invited admin never sees Step 2 at
        // all, so this is always empty on that path) — `|| undefined` so an
        // empty string is never sent as a real answer.
        productionVolume: productionVolume || undefined,
        priceId: selectedPriceId,
        acceptedTerms: true,
        termsVersion: TERMS_AND_CONDITIONS_VERSION,
      })
      advanceToPayment(secret)
    } catch (err) {
      // VERA-1095 (double-charge guard): checked BEFORE the message mapping
      // below, because this 409 is not a message at all — it is a redirect into
      // verification. Falling through would render "We've already received a
      // payment…" as a plain error above an intact plan step, which reads as a
      // dead end and leaves the visitor's real next action (confirm it) hidden.
      // This is the "Back to signup" path from the success page's verify-failure
      // state: the visitor whose payment succeeded but whose confirmation did
      // not, walking back to pay a second time.
      if (handleAlreadyPaid(err)) {
        // Cleared unconditionally. In the redirect case the browser is leaving
        // anyway; in the no-id case the visitor stays on this step with an
        // Alert and must not be left holding a permanently spinning CTA.
        setLoading(false)
        return
      }
      // VERA-1095: same WorkOS strength-error mapping as PasswordSetup — the
      // client-side pattern rule above should already block a non-compliant
      // password before this ever fires, but if it somehow doesn't (e.g. a
      // password manager's autofill bypassing the change handler), don't
      // relay WorkOS's raw "does not meet strength requirements" string.
      setError(mapWorkosPasswordError(extractMessage(err, 'Could not start checkout. Please try again.')))
      setLoading(false)
    }
  })

  /*
    Editing the email after later steps are revealed invalidates the eligibility
    answer they are built on — collapse back to the gate rather than let a stale
    allow / company-match silently carry over to a different address.

    VRG-595: mostly UNREACHABLE now, and kept anyway. The only email input on
    the page is the gate's own, and reaching it means eligibility has ALREADY
    been reset by `goBack` landing on index 0… except it hasn't: `goBack` moves
    the requested index, it does not clear `eligibilityState`. So someone who
    presses Back from the identity step and edits their address DOES arrive
    here with `passed` still set, and this is what stops the old answer being
    reused for the new address. It is the collapse, not a belt over one.
  */
  const onEmailValueChange = (value: string) => {
    revalidateIfErrored('email')
    if (eligibilityState === 'passed' && value !== checkedEmailRef.current) {
      setEligibilityState('idle')
      setPendingCompany(null)
      setStepIndex(0)
    }
  }

  // Reusable "purple pulse" on the primary CTA while a request is in flight —
  // the same keyframe (name, easing, reduced-motion guard) AuthLogo already
  // uses while `isLoading`, applied here to the final checkout submit only.
  /**
   * REMOVED — "Continue to payment" no longer pulses and glows while it works.
   *
   * It scaled to 1.015 on a 1.4s loop with a 24px brand glow, which is a lot of
   * attention for a wait the visitor cannot affect, and the glow put a second
   * saturated purple element on a page that was already being pulled away from
   * exactly that (see the success mark's own note). The button already reports
   * itself: `AuthPrimaryButton` swaps its label for a spinner while `loading`,
   * keeping the primary fill, which is the same treatment every other step's
   * CTA uses. Two loading signals on one control is one too many.
   *
   * Kept as a named empty object rather than deleting the prop at the call site,
   * because the `sx` spread there is also how a caller layers anything else onto
   * this button later; removing it would take the seam with it.
   */
  const pulseSx = {}

  // VERA-1095: Step 1 ("identity") now shares FORM_WIDTH with Step 2
  // ("company") instead of its own narrower NARROW_WIDTH — Spencer's call,
  // done FIRST and independent of the step-1-clips-on-back-nav bug, on the
  // reasoning that a single width across every "gate"-phase step removes an
  // entire class of width-change-across-steps bug by construction (and a
  // single width is also one less axis of motion during the step transition,
  // the same reason the cross-fade replaced the horizontal slide). Step 3
  // ("plan") keeps its own, genuinely wider WIDE_WIDTH — it holds a 3-column
  // plan comparison table that does not fit at FORM_WIDTH. `phase !== 'gate'`
  // (not-eligible / confirm-join / joined) is a different set of screens
  // entirely and keeps NARROW_WIDTH, unrelated to this change.
  const containerWidth =
    phase !== 'gate'
      ? NARROW_WIDTH
      : currentStepKey === 'plan'
        ? WIDE_WIDTH
        : currentStepKey === 'email'
          ? GATE_WIDTH
          : FORM_WIDTH

  // VRG-595: the eligibility spinner moved to the GATE with the check itself.
  // The identity step no longer makes a request, so it has no in-flight state.
  const continueLoading =
    currentStepKey === 'email'
      ? eligibilityState === 'checking'
      : currentStepKey === 'plan'
        ? loading
        : currentStepKey === 'payment'
          ? paying
          : false
  // Continue stays ENABLED on the form steps. It is the counterpart to validating
  // on submit rather than on change: a button disabled until the form is valid,
  // on a form that only reveals what is wrong once you press it, is a dead end —
  // the user cannot press the thing that would tell them what is missing, and the
  // disabled state itself says nothing about which field is at fault.
  //
  // `onContinueFromIdentity` / `onContinueFromCompany` already `trigger()` their
  // own field set and bail before doing any work, so pressing it while incomplete
  // costs nothing and surfaces the errors. This deliberately departs from the
  // house "disable submit while a field errors" habit, which suits a form whose
  // errors are already visible — not this one.
  //
  // The plan step keeps its guard: there is genuinely nothing to submit without a
  // selected price, and a plan is preselected from production volume anyway, so
  // in practice it is never disabled either.
  // The payment step is disabled until `PaymentElement` has actually mounted —
  // `confirmPayment` before that rejects with something no visitor can act on,
  // so the honest state is a dead button beside a skeleton rather than a live
  // one that errors.
  const continueDisabled =
    currentStepKey === 'email'
      ? emailIncomplete
      : currentStepKey === 'identity'
        ? identityIncomplete
        : currentStepKey === 'company'
          ? companyIncomplete
          : currentStepKey === 'plan'
            ? !selectedPriceId || (!pendingCompany && !acceptedTerms)
            : currentStepKey === 'payment'
              ? !submitPayment
              : false
  const continueLabel =
    /*
      VRG-595: the GATE has no branch here on purpose — it takes the trailing
      default, plain "Continue", the same label every other non-terminal step
      uses. A `currentStepKey === 'email' ? 'Continue'` branch would return the
      default and read as dead code to the next person.

      "Check my invite" was the earlier label and was rejected together with the
      "Check your invite" title above it. The argument for it was that a CTA
      must not promise an account this screen may refuse. That still holds, and
      "Continue" does not breach it: it advances a form and asserts nothing
      about what is being created. What "Check my invite" DID assert was the
      product's stage rather than the visitor's next move.
    */
    currentStepKey === 'plan'
      ? 'Continue to payment'
      : currentStepKey === 'payment'
        // Names the COMMITMENT, the amount, and the CADENCE. This is the click
        // that charges a card, and the number is the one thing someone wants
        // confirmed at that moment — it is also the last chance to catch a plan
        // picked by mistake two steps ago.
        //
        // The cadence is the part that was missing and it was a real defect, not a
        // polish item: "Pay $99" on a SUBSCRIPTION reads as a one-time charge. The
        // one place a recurring commitment must be unambiguous is the button that
        // starts it.
        //
        // Deliberately not aspirational ("Start connecting"). That belongs in the
        // step description, where it costs nothing; on the charge button it would
        // replace the amount with a slogan at exactly the moment consent matters.
        ? selectedPlan
          ? `Subscribe · ${centsToDollar(selectedPlan.amount)}/${showAnnual ? 'year' : 'month'}`
          : 'Subscribe'
        : 'Continue'
  // VERA-1095: the payment step DOES have a CTA of ours now.
  //
  // Under embedded Checkout it could not: Stripe's iframe owned its own submit
  // button, so a second one beneath it would have fought it. With Elements we
  // own the form, so the step gets the same full-width `AuthPrimaryButton` in
  // the same footer position as every other step, beneath the same error Alert
  // — instead of a purple Stripe button in a different place with different
  // styling. `submitPayment` is handed up by SignupPaymentStep (it can only be
  // created inside `<Elements>`; see that file's `registerSubmit`).
  // Hidden while the payment-failure panel is up. That panel carries its own
  // primary action ("Start payment again"), and there is no card form behind it
  // for a "Subscribe · $29/month" button to submit — leaving it there would be
  // a disabled primary CTA sitting under a live one, which reads as the page
  // having broken rather than as a step that can be repeated.
  //
  // VRG-595: also hidden while an invite is resolving. The gate's panel is a
  // skeleton at that moment (§19), and a live Continue under a skeleton invites
  // a click that would check an email the visitor cannot see and has not
  // confirmed.
  const showContinue =
    !(currentStepKey === 'payment' && paymentFailure) &&
    !(currentStepKey === 'email' && inviteResolving)
  const onContinueClick =
    currentStepKey === 'email'
      ? onContinueFromEmail
      : currentStepKey === 'identity'
        ? onContinueFromIdentity
        : currentStepKey === 'company'
          ? onContinueFromCompany
          : currentStepKey === 'plan'
            ? onSubmitCheckout
            : currentStepKey === 'payment'
              ? () => {
                  void submitPayment?.()
                }
              // `undefined` rather than an empty arrow, so an unexpected step
              // leaves the button genuinely inert instead of silently swallowing
              // clicks.
              : undefined

  /**
   * VERA-1095: Enter submits the current step.
   *
   * This page is a div layout with an onClick CTA, not a `<form>`, so Enter did
   * nothing at all — you filled four fields and then had to reach for the
   * mouse. Handled here rather than by wrapping the page in a `<form>`: any
   * `<button>` inside a form without an explicit `type` becomes a SUBMIT
   * button, and this page carries several (the password reveal toggle, Back,
   * the plan cards), so a form element would turn each of them into a way to
   * submit the step by accident.
   *
   * Guards, each for a specific way this goes wrong:
   * - Only from an INPUT. Enter on a focused BUTTON must activate that button,
   *   and in a TEXTAREA it must insert a newline.
   * - Not while a listbox is open (`aria-expanded`). The country select and the
   *   categories multi-select use Enter to CHOOSE an option; submitting the
   *   step out from under an open dropdown would be hostile.
   * - Only in the `gate` phase, which is the only one that renders the stepper.
   *   The other phases (joined / not-eligible / confirm-join) have their own
   *   CTAs, and `onContinueClick` is meaningless there.
   * - Respects `continueDisabled` and `loading`, so Enter can never do what a
   *   click at that moment could not.
   */
  const onStepKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter' || phase !== 'gate') return
    const target = event.target as HTMLElement
    if (target.tagName !== 'INPUT') return
    if (target.getAttribute('aria-expanded') === 'true') return
    // `continueLoading`, NOT the bare `loading`. This was the Enter-is-unreliable bug.
    //
    // `loading` is the confirm-join / checkout flag (set by `onConfirmJoin` and
    // `onSubmitCheckout`). It has nothing to do with the identity or company steps,
    // where `continueLoading` resolves to `eligibilityState === 'checking'` instead.
    // So this guard was reading a flag owned by a DIFFERENT step, and it failed in
    // both directions:
    //
    //   - Once anything had set `loading` true and left it there — a checkout
    //     attempt that errored out, say — Enter was dead on every step for the rest
    //     of the session, while the Continue BUTTON kept working, because the button
    //     only consults `continueDisabled` and takes `continueLoading` for its
    //     spinner. That asymmetry is exactly what "Enter is unreliable" looked like:
    //     inert from the same state a click succeeded from, and only after you had
    //     been as far as the plan step.
    //   - Inversely, while eligibility WAS in flight on the identity step, `loading`
    //     is false, so Enter was NOT blocked and could fire a second check on top of
    //     the first.
    //
    // `continueLoading` is the per-step signal the button already uses, so keying
    // both off it means the keyboard and the mouse can no longer disagree about
    // whether this step is busy.
    if (continueDisabled || continueLoading) return
    event.preventDefault()
    // Optional-called: `onContinueClick` is undefined on any step with no
    // Continue of its own. It never has been on a step Enter can reach from an
    // INPUT (the payment step's inputs are inside Stripe's iframes, whose
    // keystrokes never reach this handler), but the guard costs nothing and the
    // alternative is a runtime throw the moment that stops being true.
    onContinueClick?.()
  }

  /*
    VRG-595 — the invite banner's BRAND-GRADIENT BORDER.

    A rotating variant was built alongside this one and compared side by side
    behind a temporary `?border=animated` URL param. The STATIC treatment won;
    the param, the conic gradient, the `veraInviteBorderRotate` keyframe and the
    `@property --vera-invite-border-angle` registration in `index.css` all went
    with the decision, because a live A/B switch left on a public signup page is
    a surface nobody owns.

    WHY A MASKED PSEUDO-ELEMENT AND NOT `border-image` OR A GRADIENT WRAPPER.
    A gradient is an image, so `border-color` cannot carry one and `bgcolor`
    would silently drop it (the same trap the step-numeral ramp documents).
    Of the three real techniques:

      - `border-image` does not follow `border-radius`. It would paint a
        gradient frame with SQUARE corners around the Alert's 8px radius, which
        is worse than the grey border it replaces.
      - a gradient-backed WRAPPER (`background: paper padding-box, gradient
        border-box`) works, but it has to restate the Alert's own surface
        colour, and `@repo/ui`'s Alert resolves that from mode-aware severity
        tokens. Restating it is a second declaration that drifts the day
        anyone edits the first.
      - a MASKED ::before paints a 1px ring at the element's own edge and
        touches nothing else. It inherits `border-radius`, leaves the Alert's
        background, padding and dark-mode behaviour entirely alone, and needs
        to know nothing about either.

    The Alert is already `position: relative` in its own styled block, so the
    absolutely-positioned ::before lands on it without a layout change. The
    Alert's real 1px border is turned transparent so the grey does not peek out
    from under the ring.

    THE GRADIENT IS the `brandRampStops` ramp the step numerals already use,
    read from `authFieldChrome` rather than restated (§28/§47). It inverts with
    mode by design (dark purple in light, light purple in dark), so the border
    reads on both pages for free.

    It is laid as a 90deg linear gradient — not the ramp's 135deg default,
    because the banner is a wide, short box and a diagonal across it resolves to
    almost a horizontal anyway.

    NOTHING HERE MOVES, so there is no `prefers-reduced-motion` branch to write:
    the border is information (this is an invite) and carries no motion to drop.
  */
  const [rampFrom, rampTo] = brandRampStops(theme)
  const inviteBannerBorderSx = {
    // The Alert's own severity border, made invisible so the ring below is the
    // only edge. Not `border: none` — removing the border would change the
    // element's box by 1px on each side and shift the copy inside it.
    borderColor: 'transparent',
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      // Inherited rather than restated, so the ring can never disagree with the
      // Alert's radius if that radius changes in `@repo/ui`.
      borderRadius: 'inherit',
      // The ring's thickness: the mask below keeps only the 1px padding band.
      padding: '1px',
      // The ramp once, left to right.
      background: `linear-gradient(90deg, ${rampFrom}, ${rampTo})`,
      /*
        The ring itself: paint the whole rounded box, then subtract the content
        box, leaving only the 1px `padding` band. `-webkit-` first for Safari,
        which still needs the prefixed property and spells the operation `xor`
        where the standard spells it `exclude`.
      */
      WebkitMask:
        'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
      WebkitMaskComposite: 'xor',
      mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
      maskComposite: 'exclude',
      // A decoration, never a target — it covers the whole banner including its
      // copy, so without this it would eat every pointer event inside it.
      pointerEvents: 'none',
    },
  } as const

  const stepTitle =
    pendingCompany && currentStepKey === 'plan' ? 'Choose your plan' : STEP_TITLE[currentStepKey]
  const stepDescription =
    pendingCompany && currentStepKey === 'plan' ? (
      <>
        <strong>{pendingCompany.name}</strong> is already set up for you. Choose a plan to finish
        activating it.
      </>
    ) : (
      STEP_DESCRIPTION[currentStepKey]
    )

  // NOTE: the inner JSX keeps the indentation it had under the removed
  // `<ThemeProvider theme={darkTheme}>` wrapper. Re-indenting ~880 lines would
  // bury this commit's actual change (the theming) in whitespace; left for a
  // formatting pass.
  return (
      <Box
        sx={{
          /* THE AUTH SCALE (authScale.ts) — sign-in set `--auth-scale` on desktop
             from VRG-620 and this page never did, so the two halves of one flow
             rendered at two different sizes.

             ⚠️ The measurements below are from when that scale was 0.8. VRG-784
             set it to 1 — vera.inc, the signed-in platform and this page were all
             already 1, so sign-in was the sole outlier and was brought to them
             rather than the reverse. The numbers are kept as the EVIDENCE for
             sharing one variable, not as a description of today's rendering: both
             pages now resolve it to 1 and agree, which is the property that
             mattered. Measured at 1280x800 on
             d59b0e561, the SAME footer sentence read 12px on sign-in and 15px
             here, the primary button 12.8px against 16px, and sign-up's card
             ran off the bottom of an 800px viewport while sign-in's fitted.
             The Back control made it worse rather than better: `AuthBackButton`
             sets the scale on ITSELF (it is portaled, so it cannot inherit),
             so the corner chrome was at 80% beside a 100% card.

             Spencer, 2026-10-28, with the two screens side by side: "the bottom
             text are not the same size - these should all match".

             Nothing in THIS file is written in `authPx`, so the variable reaches
             only the shared auth components the page renders — the glass card,
             AuthLogo, AuthPrimaryButton, AUTH_TITLE_SX, the field chrome and
             Back. That is the intended blast radius; see the two hand-written
             sizes converted below, and the `@repo/ui` note on the field label. */
          ...authScaleSx,
          // Structurally IDENTICAL to sign-in's shell: fixed, full-bleed,
          // `overflow: hidden`, with SignInBackground as a DIRECT child. The
          // scrolling moved to an inner element (below) instead of living here.
          //
          // That structure is load-bearing for how the background COMPOSITES, not
          // just for scrolling. SignInBackground's grain layer uses
          // `mixBlendMode: 'overlay'`, which blends against the backdrop within its
          // own STACKING CONTEXT. Wrapping the background in its own
          // `position: fixed` layer — which is what this page did to stop the blobs
          // generating phantom scroll — created a new stacking context and isolated
          // the grain from the page's black, so it blended only against the blobs.
          // The result read visibly grainier and more purple than sign-in even
          // though both render the same component with the same blob geometry.
          position: 'fixed',
          inset: 0,
          overflow: 'hidden',
          // Sign-in's literal `#000000`, not `background.default` (zinc-950,
          // #09090B). The two pages navigate straight into each other, so a base
          // colour differing by even one step reads as a flash at the swap.
          // VERA-1095: backdrop hoisted to LayoutAuthAmbient — see there.
          bgcolor: 'transparent',
          zIndex: 10,
        }}
      >

        {/* Vertically centred like sign-in, via `margin: auto` on the CHILD rather
            than `justifyContent: 'center'` on the container.

            That distinction is the whole point. `justify-content: center` on a
            scrollable flex container centres a child taller than itself by pushing
            its overflow ABOVE the scroll origin, where no amount of scrolling can
            reach it — the trap LayoutAuth documents and this file previously dodged
            by refusing to centre at all. `margin: auto` centres only the space that
            is actually spare, so a short step (1 and 2, and now 3 with Compare plans
            collapsed) sits centred, and a step taller than the window simply starts
            at the top and scrolls normally instead of losing its head.

            So: centred when it fits, never clipped when it does not. `overflowX` is
            hidden on the ancestor, so nothing scrolls sideways either. zIndex:2
            keeps content above SignInBackground's blobs (zIndex:1). */}
        {/* The SCROLLER, moved off the shell so the shell can stay `overflow:
            hidden` like sign-in's. Content taller than the window (a long plan
            step on a short screen) scrolls here; the background stays put. */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            height: '100%',
            overflowY: 'auto',
            // Nothing scrolls sideways: the plan step widens to 1120px, wider than
            // a narrow window, and this keeps that wrapping rather than scrolling.
            overflowX: 'hidden',
            // A VISIBLE scroll affordance. This container already scrolls whenever
            // content exceeds the viewport (confirmed: real wheel/trackpad and
            // keyboard scroll both reach it — it's the shell ABOVE it, per that
            // Box's own comment, that stays `overflow:hidden` on purpose to match
            // sign-in). The gap was discoverability, not function: macOS defaults
            // to auto-hiding scrollbars until an active scroll gesture, and on a
            // pure-black page that gives zero visual cue a short window even HAS
            // more content below the fold — someone who never scrolls never finds
            // Continue. A thin, always-on bar (both engines) makes "there's more
            // here" visible without touching the shell's fixed/hidden structure.
            // The thumb is `alpha(text.primary, 0.25)` rather than a white
            // alpha: a scrollbar is chrome ON the page, so it has to contrast
            // with the page in whichever direction the page happens to sit.
            scrollbarWidth: 'thin',
            scrollbarColor: `${alpha(theme.palette.text.primary, 0.25)} transparent`,
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(theme.palette.text.primary, 0.25),
              borderRadius: 3,
            },
          }}
        >
        {/* VERA-1095: py trimmed 10 -> 5 (40px -> 20px each side). Spencer's
            explicit constraint: the whole block must fit inside 1280x680 with
            no vertical scroll — that requires removing real dead space, not
            hiding it behind a scrollbar, and this outer padding was pure
            margin with nothing depending on the larger value. */}
        <Stack alignItems="center" sx={{ minHeight: '100%', pt: 12, pb: 5, px: 3 }}>
          {/* `data-auth-column`: the ambient lobes centre on this (SignInBackground
              useLogoAnchor) — the whole form, as on sign-in. */}
          <Box
            data-auth-column=""
            sx={{
              // The liquid-glass card, shared with sign-in (authGlassCard.ts).
              //
              // `density: 'solid'` DROPPED 2026-10-30. It was set on 2026-10-08
              // for "less of the blobs showing", but sign-in kept the default, so
              // the same card read as two different materials depending on which
              // door you arrived through. `CARD_TINT` is now the midpoint of the
              // two and both cards use it, which keeps the 2026-10-08 intent (the
              // lobes still read as ambient, not busy) without the mismatch.
              //
              // Phones are UNAFFECTED: the sheet below `sm` is solid for every
              // auth page unconditionally, never via this prop.
              ...authGlassCardSx(theme),
              width: '100%',
              /* `authPx`, so the CARD follows `--auth-scale` with the type inside
                 it. GATE_WIDTH's own docblock pins it to "sign-in's column,
                 exactly" — and that pinning broke silently the day sign-in took
                 the 0.8 desktop scale, because 396 is a bare number and sign-in's
                 396 became 317. Measured on d59b0e561 at 1280x800: sign-in's
                 column 317px, this one 370px, from a constant whose whole purpose
                 was to make them equal. A width pinned to another surface has to
                 follow that surface's scale or the pin is decorative. */
              maxWidth: authPx(containerWidth),
              // Centres vertically when there is spare room; collapses to 0 when
              // there is not, which is what makes tall steps still reachable.
              my: 'auto',
              // A single ~150ms fade of the whole content block ON MOUNT ONLY —
              // deliberately NOT a staggered per-field entrance (discussed and
              // rejected: it delays the first field on a form people came to
              // type into, and this page is commonly returned to after
              // abandoning at the plan step, where a stagger would get
              // tiresome on every repeat visit). This Box never unmounts
              // across a step/phase change (only its children swap via
              // `translateX` / conditional rendering inside it), so the CSS
              // animation — which is not `infinite` — fires exactly once, at
              // real page load, and never replays when the stepper advances.
              // Scoped to this content Box only, not the ambient
              // `SignInBackground` layer above, which is a separate fixed
              // sibling outside this subtree.
              // VERA-1095: now the incoming half of the sign-in <-> sign-up
              // cross-fade, not just a page-load fade. Was opacity-only over
              // 150ms; it now enters moving up from BELOW over
              // AUTH_TRANSITION_MS, so it mirrors the outgoing page (which
              // leaves moving UP) and matches the stepper's panel entrance
              // exactly. One shared constant, because the two halves being
              // different durations is precisely what read as a flash.
              '@keyframes veraSignupContentFadeIn': {
                from: { opacity: 0, transform: 'translateY(10px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
              // Matches the existing height/transform transitions' own
              // reduced-motion handling below: disable entirely via the JS
              // boolean rather than shortening the duration.
              animation: prefersReducedMotion
                ? 'none'
                : `veraSignupContentFadeIn ${AUTH_TRANSITION_MS}ms ease-out`,
              // Exit, for the "Already have an account? Sign in" link. Listed
              // last so it overrides the entrance if a click lands mid-fade.
              ...authExitSx,
              // COMPOSED, not spread. `authExitSx` carries its own `transition`
              // for opacity+transform, and spreading it last silently replaced
              // this Box's `max-width 0.3s ease` — the animation that widens the
              // container when the plan step reveals its four columns. The
              // widening snapped instead of easing, and nothing on screen said
              // why. TypeScript caught it (TS2783, "specified more than once"),
              // which is the only reason it did not ship.
              //
              // So the two are merged here rather than one winning. Durations
              // come from the shared constant so this cannot drift from the
              // hook, and the reduced-motion branch keeps `max-width` — that one
              // is a layout accommodation, not decoration, and losing it makes
              // the container jump.
              transition: prefersReducedMotion
                ? 'max-width 0.3s ease'
                : `max-width 0.3s ease, opacity ${AUTH_TRANSITION_MS}ms ease, transform ${AUTH_TRANSITION_MS}ms ease`,
            }}
            // Enter submits the current step. On the wrapper rather than each
            // field, so it keeps working as fields are added — keydown bubbles.
            onKeyDown={onStepKeyDown}
          >
            {/* VERA-1095: logo->content gap trimmed 40px -> 24px, part of the
                same no-vertical-scroll-at-1280x680 budget as the outer `py`
                above. Not a shared value with sign-in — that page uses its own
                60px via a fixed-width, always-short-content shell (no stepper,
                no checklist, never needs to fit a tall step), so tightening
                this here doesn't touch anything sign-in depends on. */}
            {/* 24 -> 40px (2026-09-23): the Back row that sat between the logo
                and the title moved into the card's corner and returned 44px of
                the 1280x680 budget this gap was trimmed to meet. Re-measured at
                1280x680: steps 1-3 still fit with no scroll. */}
            <Stack alignItems="center" spacing="40px" sx={{ width: '100%' }}>
              {/* CENTRED (user call, 2026-08-25). It was briefly left-aligned
                  with the content beneath it, on the argument that a centred
                  mark floats unmoored above a left-aligned header. Reverted:
                  the wordmark is page furniture rather than the first item of
                  the header, and it reads as the page's own mark when centred —
                  which is also how the sign-in page carries it, so the two
                  surfaces stay consistent across the cross-fade between them. */}
              <AuthLogo isLoading={loading} />

              <Box sx={{ width: '100%' }}>
                {/* -- Success: employee joined an existing company ------------ */}
                {phase === 'joined' && (
                  <Box sx={{ maxWidth: FORM_WIDTH, mx: 'auto' }}>
                    <Typography sx={{ ...PAGE_TITLE_SX, mb: "8px" }}>
                      You're in
                    </Typography>
                    <Divider />
                    <Typography variant="body1" mt="24px" mb="24px" sx={{ lineHeight: 1.5 }}>
                      Your account has been added to your company on Vera. Sign in with your
                      email to get started.
                    </Typography>
                    <AuthPrimaryButton fullWidth onClick={() => navigate(ROUTES.SIGN_IN)}>
                      Go to sign in
                    </AuthPrimaryButton>
                  </Box>
                )}

                {/* -- Not eligible: the email wasn't invited ------------------ */}
                {phase === 'not-eligible' && (
                  <Box sx={{ maxWidth: FORM_WIDTH, mx: 'auto' }}>
                    <Typography sx={{ ...PAGE_TITLE_SX, mb: "8px" }}>
                      Sign up
                    </Typography>
                    <Divider />
                    <Alert
                      severity="info"
                      /*
                        VRG-595: support is REACHABLE here, not just named.

                        The sentence used to end "or contact support" as plain
                        text, which tells someone who has just been refused that
                        a route exists and then makes them go and find it. This
                        is the one screen where the reader has no account, no
                        in-app support intake and nowhere else to go, so the
                        address has to be on it.

                        An inline link, not a button: "Try a different email"
                        below is the primary action, and a second button of
                        equal weight competes with it for the reader who mostly
                        just mistyped their address.

                        ⚠️ DO NOT reintroduce "yet" or "currently" here. The
                        sentence read "hasn't been invited to Vera YET. Signups
                        are CURRENTLY invite-only", and both words say the same
                        unintended thing: that the gate is a phase Vera is
                        passing through. This is the ONE screen shown to somebody
                        who has been turned away, so it is the last impression a
                        rejected visitor takes with them, and "we aren't ready
                        for you" is a materially different message from "you need
                        an invite". Vera sells provenance and trust; a maturity
                        admission is a positioning cost. The whole family is
                        banned on this page: "right now", "for now", "at the
                        moment", "currently", "yet", "still", beta, waitlist.
                        Deleting both words changes no fact — signups ARE
                        invite-only — it only stops the sentence apologising for
                        it.

                        The link TEXT is the address itself. `mailto:` does
                        nothing on webmail with no registered mail client, and
                        when it does nothing the words still have to BE the
                        answer — "contact support" would leave them with a dead
                        click and no address.
                      */
                      description={
                        <>
                          This email hasn't been invited to Vera. Signups are
                          invite-only. If you believe this is a mistake, reach out to the person
                          who invited you. You can also email{' '}
                          <AuthInlineLink href={SUPPORT_MAILTO}>{SUPPORT_EMAIL}</AuthInlineLink>.
                        </>
                      }
                      sx={{ mt: '24px' }}
                    />
                    <Stack mt="24px">
                      <Button
                        variant="secondary"
                        fullWidth
                        onClick={() => {
                          setPhase('gate')
                          setError(null)
                        }}
                      >
                        Try a different email
                      </Button>
                    </Stack>
                  </Box>
                )}

                {/* -- Confirm join: explicit confirmation before /signup/join
                    writes anything --------------------------------------------- */}
                {phase === 'confirm-join' && (
                  <Box sx={{ maxWidth: FORM_WIDTH, mx: 'auto' }}>
                    <Typography sx={{ ...PAGE_TITLE_SX, mb: "8px" }}>
                      Join {matchedCompany?.name ?? 'this company'}?
                    </Typography>
                    <Divider />
                    <Typography variant="body1" mt="24px" mb="32px" sx={{ lineHeight: 1.5 }}>
                      <strong>{getValues('email')}</strong> matches an existing Vera company.
                      {matchedCompany?.name ? (
                        <>
                          {' '}
                          <strong>{matchedCompany.name}</strong>.
                        </>
                      ) : null}{' '}
                      Confirm to join it as a team member.
                    </Typography>

                    {error && <Alert severity="error" description={error} sx={{ mb: '24px' }} />}

                    <Stack direction="row" spacing={3}>
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setPhase('gate')
                          setMatchedCompany(null)
                          setError(null)
                        }}
                      >
                        Back
                      </Button>
                      <AuthPrimaryButton fullWidth loading={loading} onClick={onConfirmJoin}>
                        Join company
                      </AuthPrimaryButton>
                    </Stack>
                  </Box>
                )}

                {/* -- Gate: the 3-step stepper ---------------------------------- */}
                {phase === 'gate' && (
                  <Stack sx={{ width: '100%' }}>
                    {/* Width follows the CONTENT below it, not a fixed 640.
                        On the plan step the matrix is 1120 wide, so a 640 header
                        put "Choose your plan" 240px inside the grid's left edge
                        and the two read as unrelated blocks. `containerWidth` is
                        already 640 on steps 1-2, so those are unchanged. */}
                    {/* `authPx` for the same reason as the card above — this
                        header must stay the width of the content under it. */}
                    <Box sx={{ maxWidth: authPx(containerWidth), mx: 'auto', width: '100%' }}>
                      {/* Back control — a FIXED slot, rendered on ALL THREE steps in
                          the IDENTICAL position, always LEFT-aligned (back means
                          leftward in every convention a visitor already knows).
                          Previously lived INSIDE the step-indicator row below,
                          right-aligned via `justifyContent="space-between"`, and
                          hidden entirely on Step 1 along with that whole row.
                          Pulled into its own always-rendered row so nothing below
                          it ever has to renegotiate position.

                          Same BUTTON — same `variant="text"` + chevron + label —
                          just larger (`size="medium"`, 40px, was `"small"` at
                          32px): the control read undersized specifically on
                          hover, where the text-variant's hover highlight box is
                          sized to the button's own box, and 32px made that
                          highlight feel cramped around "Back". Text size is
                          unaffected by `size` on this component (fixed at 16px);
                          only the box/hit-area and its hover highlight grow.

                          Step 1 has no step to go back to, so Back there returns
                          to SIGN-IN (user directive, 2026-08-25). It previously
                          left the app entirely for the marketing site, on the
                          reasoning that "Already have an account? Sign in" below
                          the CTA already covered that destination and two
                          controls should not point at one place.

                          That reasoning does not hold. The two express different
                          intents and are reached from different mental states:
                          "Back" means UNDO MY LAST NAVIGATION, while "Already
                          have an account" is a statement about yourself. More
                          concretely, most people who reach this page arrive from
                          sign-in via "Create one", so sign-in IS what Back is
                          undoing — and sending them to a marketing site instead
                          is a genuinely surprising destination for a control
                          whose whole promise is "put me back where I was".

                          Uses the same cross-fade as the other cross-link, so
                          the two routes into sign-in look identical.

                          VRG-595: the "first step" is `minStepIndex`, not a
                          literal 0. For a non-invited visitor that is the gate,
                          unchanged. For an invited one the invite path
                          auto-advanced past the gate, so THEIR first step is
                          identity — reversing them into a screen they never saw
                          would be the same surprise this comment argues against
                          two paragraphs up, just pointed inward. */}
                      {/* BACK LIVES IN THE SCREEN'S TOP-LEFT CORNER — mirroring the
                          theme toggle top-right (LayoutAuthAmbient). Spencer,
                          2026-09-23, after trying it in the card's corner: "move
                          back to top left of the screen so it doesn't compete with
                          the card". Then, same day: "Back button should be a text
                          button like the light/dark mode selector" — so it now
                          shares the toggle's treatment (56% ink, 6% plate on
                          hover, regular weight throughout; bold-on-hover is gone).
                          Portal, fixed corner and zIndex 20 (above this page's
                          own z-index 10 shell) all live in `AuthBackButton`,
                          which sign-in uses too; see its comment for why each is
                          load-bearing. */}
                      <AuthBackButton
                        onClick={
                          stepIndex <= minStepIndex
                            ? () => leaveToSignIn(ROUTES.SIGN_IN)
                            : goBack
                        }
                      />
                      {/* VERA-1095: the invited-arrival banner. Resolve-first, render-
                          second — `inviteEmail` is only set once the token has
                          round-tripped through `POST /signup/resolve-invite` and come
                          back `valid:true`, so a stranger who appends `?invite=x` to
                          the URL never sees this (an absent/invalid token renders
                          nothing extra, per this file's own resolve effect). Scoped to
                          Step 1 only — the moment the copy ("Create your Vera
                          account") actually names — rather than persisting across the
                          whole stepper, since Steps 2/3 have their own titles this
                          banner was never written to sit above. */}
                      {currentStepKey === 'identity' && inviteEmail && (
                        <Alert
                          severity="info"
                          icon={
                            /* VRG-595: an opened envelope, not sparkles. Sparkles is the
                               near-universal AI signal and there is no AI here. The Vera
                               `VeraMark` was considered and rejected: it is already the
                               Ask-Vera/assistant mark (AskPanel, AskVeraLauncher,
                               SearchOverlay, AskAboutButton), so it would read as the
                               assistant rather than as an invite. `UserPlusIcon` is §21's
                               icon for the invite ACTION an admin takes, which is the
                               wrong point of view for a banner the INVITEE reads. The
                               banner's own copy is "This is the email your invite was
                               sent to", so an opened envelope names the actual thing. */
                            <MailOpenIcon
                              size={18}
                              strokeWidth={1.5}
                              color={theme.palette.brand[500]}
                            />
                          }
                          /*
                            VRG-595 — PERSONALISED WHEN WE HAVE A NAME, and the
                            same line, unadorned, when we do not.

                            Folded into the EXISTING title rather than added as a
                            third line. The invite path already carries an alert
                            title and a page heading ("Create your Vera
                            account"); a separate greeting above one email field
                            would be three celebratory lines before any content.

                            The un-named branch is the ORDINARY one, not a
                            fallback for an error: staff allowlist people
                            directly and those invitees have no waitlist row at
                            all, so `resolve-invite` returns no name. Both
                            branches are complete sentences of the same length
                            class, so nothing shifts between them — there is no
                            "You're in, !" state to reach because
                            `normaliseInviteName` has already turned empty and
                            whitespace-only into null.
                          */
                          title={
                            inviteFirstName
                              ? `You're in, ${inviteFirstName}`
                              : "You've received an exclusive invite"
                          }
                          /*
                            §15: no em-dashes in UI copy; descriptive body copy gets
                            lineHeight 1.5 so it doesn't read dense.

                            VRG-595: the wrapping `<Box sx={{ lineHeight: 1.5 }}>` that
                            used to be here is GONE, and it was two faults at once.

                            It was a DOM-nesting bug: `Alert` renders `description`
                            inside a `<Typography>`, which is a `<p>`, so a `Box` put a
                            `<div>` inside a `<p>`. React logged `validateDOMNesting` on
                            every render of this banner, and the browser's HTML parser
                            treats a block element inside `<p>` as an implicit close of
                            that paragraph — so the DOM the browser built never matched
                            the tree React thought it had rendered.

                            And it was redundant: the comment it carried said "Alert's
                            own Typography doesn't expose a lineHeight prop", which is no
                            longer true of the shipped component. `Alert.tsx:579` sets
                            `lineHeight: 1.5` on that Typography itself, so the Box was
                            re-declaring a value it would have inherited anyway. Deleting
                            it changes no computed style (measured: 24px before and
                            after) while removing the invalid nesting.

                            So the local fix is a DELETION, not a swap to `component="span"`.
                            A span would have been valid and inert — but it would have left
                            a wrapper whose only stated purpose no longer applies, which is
                            the kind of thing the next reader keeps because they cannot tell
                            what it is for.
                          */
                          /*
                            ⚠️ DO NOT reintroduce "early access", and do not
                            replace it with "beta", "waitlist", "for now" or
                            anything else from that family.

                            The line was "We hand-picked you for EARLY ACCESS to
                            Vera." Early access describes a product's stage, not
                            the reader's standing, so it quietly reframes the
                            invitation: instead of "you were chosen", it says
                            "we are not finished, and you are here early". Same
                            positioning cost as the "yet"/"currently" pair on the
                            refusal alert above, arriving at the opposite moment —
                            this is the first thing an ACCEPTED invitee reads.

                            What is kept is the selectivity, which is the part
                            doing the work: "hand-picked" says a person chose
                            them. "to join Vera" replaces the stage claim with a
                            destination and says nothing about maturity either
                            way. Note the sibling title "You've received an
                            exclusive invite" stays — scarcity is a claim about
                            the invitation, not an admission about the product.
                          */
                          description={
                            <>
                              We hand-picked you to join Vera. Let&rsquo;s get
                              your account set up.
                            </>
                          }
                          sx={{ mb: '24px', ...inviteBannerBorderSx }}
                        />
                      )}
                      <Typography sx={{ ...PAGE_TITLE_SX, mb: "8px" }}>
                        {stepTitle}
                      </Typography>
                      {/* §40: page description sits directly under the title. */}
                      <Typography
                        variant="body2"
                        mt="8px"
                        color="text.secondary"
                        /* body2's 16px is a theme constant and does not follow
                           `--auth-scale`. Left alone it became the largest text on
                           a scaled card after the title, so it is restated at the
                           same number through `authPx`. */
                        sx={{ fontSize: authPx(16), lineHeight: 1.5 }}
                      >
                        {stepDescription}
                      </Typography>

                      {/* Step indicator — HIDDEN on Step 1 (item 2): on arrival
                          the form should look like one short thing, not a
                          three-part commitment. Appears from Step 2 onward.
                          The Back control used to live in this same row
                          (right-aligned) — it now has its own always-rendered
                          slot above the title (see that Box's own comment).

                          The row now carries ONE right-aligned occupant: the
                          Monthly/Annual toggle, on the plan step only. It used
                          to live inside `PlanComparisonMatrix` (its
                          `intervalControl` slot, top-right of the card on
                          `centeredHeading` surfaces) — moved out here so the
                          card holds only the comparison itself and the control
                          that reframes it sits above, in the band the page
                          already uses for step-level chrome. This Box is
                          `containerWidth` wide, which is `WIDE_WIDTH` on the
                          plan step — the SAME width as the matrix below — so
                          `flex-end` here lands on the card's right edge.

                          Scoped to `currentStepKey === 'plan'`: on the company
                          and payment steps there are no prices on screen for it
                          to act on.

                          VRG-595: the row now maps `formStepKeys`, which is
                          `stepKeys` WITHOUT the eligibility gate, and compares
                          against `formStepIndex`. The gate is a screen, not a
                          step of account creation, so it is neither numbered nor
                          counted — "Your details" is still 1, exactly as it was
                          before the gate existed. `formStepIndex > 0` reproduces
                          the old `stepIndex > 0` condition one-for-one: hidden
                          on the gate (-1) AND on the first form step (0), which
                          is the "on arrival the form should look like one short
                          thing" rule above, unchanged. */}
                      {formStepIndex > 0 && (
                        <Stack sx={{ mt: '24px' }}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={3}
                          >
                            <Stack direction="row" alignItems="center" spacing={3}>
                              {formStepKeys.map((key, index) => (
                                <React.Fragment key={key}>
                                  {index > 0 && (
                                    <Box sx={{ width: 16, height: '1px', bgcolor: 'divider' }} />
                                  )}
                                  <Stack direction="row" alignItems="center" spacing={1.5}>
                                    <Box
                                      sx={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 12,
                                        fontWeight: 600,
                                        flexShrink: 0,
                                        // THE BRAND RAMP, not a flat `brand[500]`.
                                        //
                                        // Cosmetic on its face and not only that:
                                        // white on #8F68EA measures 3.94:1, so
                                        // these numerals were UNDER the 4.5:1 text
                                        // bar. Both ends of the ramp clear it in
                                        // both modes (5.00:1..11.58:1 light,
                                        // 7.24:1..4.64:1 dark) — see
                                        // `brandRampStops` for the full table.
                                        //
                                        // `background`, not `bgcolor`: a gradient is
                                        // an image, and `bgcolor` only ever sets
                                        // background-color, so it would silently
                                        // drop this on the floor.
                                        background:
                                          index <= formStepIndex
                                            ? brandRampGradient(theme)
                                            : 'transparent',
                                        color:
                                          index <= formStepIndex
                                            // INVERTS WITH MODE, which reverses what
                                            // this comment used to say. The old rule
                                            // was "the brand fill does not change
                                            // with mode, so the label on it must not
                                            // either" — correct for the CTA, whose
                                            // fill really is mode-independent, and
                                            // wrong here: the ramp runs DARK purple
                                            // in light mode and LIGHT purple in dark
                                            // (it has to, or it dissolves into a
                                            // #09090b page). A fill that inverts
                                            // takes ink that inverts with it, so
                                            // white on light and the page's own
                                            // near-black on dark. Pinning white in
                                            // both would put it at 2.75:1 on dark's
                                            // light end.
                                            ? brandRampInk(theme)
                                            : theme.palette.text.secondary,
                                        border:
                                          index <= formStepIndex
                                            ? 'none'
                                            : `1px solid ${theme.palette.divider}`,
                                      }}
                                    >
                                      {index + 1}
                                    </Box>
                                    <Typography
                                      variant="body3"
                                      color={index === formStepIndex ? 'text.primary' : 'text.secondary'}
                                      fontWeight={index === formStepIndex ? 600 : 400}
                                    >
                                      {STEP_LABEL[key]}
                                    </Typography>
                                  </Stack>
                                </React.Fragment>
                              ))}
                            </Stack>
                            {currentStepKey === 'plan' && (
                              <PlanIntervalToggle
                                checked={showAnnual}
                                onChange={handleIntervalChange}
                                savingsLabel={intervalSavingsLabel}
                              />
                            )}
                          </Stack>
                        </Stack>
                      )}
                    </Box>

                    {/* The step stack — one panel per `stepKeys` entry,
                        permanently mounted at a fixed, keyed position (see the
                        file-header comment: the `key` is what keeps two
                        different fields' DOM nodes from ever being recycled
                        into each other, and that guarantee is unchanged by
                        HOW the active step is shown, not just by keeping every
                        panel mounted).

                        CROSS-FADE, not a horizontal slide (superseded design —
                        see below for why). All panels sit in the SAME CSS
                        grid cell (`gridColumn/gridRow: 1`, i.e. stacked,
                        overlapping) and only OPACITY + a small vertical
                        TRANSFORM animate: the active panel is opacity 1 /
                        translateY(0); every other panel is opacity 0,
                        translateY(-10px) if it is a step BEHIND the current
                        one or translateY(10px) if AHEAD — so the outgoing
                        panel always exits moving up and the incoming panel
                        always enters moving up from below, regardless of
                        forward/back direction. ~200ms, `ease`, shared with the
                        WRAPPER's own height transition below so the box and
                        its content read as one movement rather than two.

                        WHY NOT THE FULL-WIDTH SLIDE THIS REPLACED: with all
                        panels sharing one `overflow: hidden` clipping
                        ancestor (needed because the wrapper's HEIGHT also
                        changes per step), an outgoing panel taller than the
                        incoming one got visibly cropped to the shrinking
                        clip-height WHILE it was still on screen and still
                        sliding — confirmed with a screenshot burst mid-
                        transition, not assumed: the outgoing panel's content
                        was cut off well above its own natural bottom edge,
                        with the card's own rounded border popping into view
                        where content should have continued. A ~640px lateral
                        travel is also a carousel gesture, more motion than a
                        form step warrants. The cross-fade's ~10px travel
                        cannot reproduce that crop — the OUTGOING panel is
                        fading to opacity 0 across the same 200ms the wrapper
                        takes to reach the new height, so by the time the
                        crop would become visible the panel is already gone.
                        This does mean the wrapper's height is genuinely
                        ANIMATING now (see below) rather than snapping — a
                        one-time snap was tried first (to a correctly-measured
                        target — see `panelHeights`' own comment) and produced
                        a DIFFERENT visible defect: the box popping to its new
                        size in one frame while the content faded smoothly,
                        which read as a flash rather than a grow.

                        `prefers-reduced-motion`: opacity-only, no vertical
                        travel — never fully instant, since a plain cross-fade
                        is one of the two explicitly acceptable reduced-motion
                        treatments (the other being no transition at all). */}
                    <Box
                      sx={{
                        display: 'grid',
                        // CSS Grid items default to `align-items: stretch` — without
                        // this override every panel STRETCHES to fill the wrapper's
                        // OWN explicit height (below), which is self-referential:
                        // `getBoundingClientRect()` on a panel then reports the
                        // WRAPPER's current height back, not the panel's real content
                        // height, so a step whose true height is short can never be
                        // measured correctly once the wrapper has ever been taller.
                        // The old flex-row track used `alignItems: 'flex-start'` for
                        // exactly this reason; `start` is the grid equivalent.
                        alignItems: 'start',
                        // The HORIZONTAL twin of the `alignItems` trap above, and it
                        // bites the same way. With no explicit template the single
                        // implicit column is `auto`, i.e. sized to the MAX-CONTENT of
                        // every panel stacked in it — not to this track's own width.
                        // All three steps share one cell, so the widest one sets the
                        // column for all of them.
                        //
                        // That stayed invisible while the plan step was narrow. The
                        // moment it became the shared plan matrix, the column went to
                        // 985.7px inside a 640px track, and steps 1 and 2 were pushed
                        // right and clipped: the card measured 921->1561 against a
                        // container of 748->1388.
                        //
                        // `minmax(0, 1fr)` is the fix rather than `1fr` alone: a bare
                        // `1fr` still floors at min-content, which is what let a wide
                        // panel expand the track in the first place. The 0 minimum is
                        // the part doing the work.
                        gridTemplateColumns: 'minmax(0, 1fr)',
                        position: 'relative',
                        width: '100%',
                        // `clip`, NOT `hidden` — and the difference is the whole fix
                        // for "clicking the volume select shifts the fields up".
                        //
                        // `overflow: hidden` clips AND makes the box a scroll
                        // CONTAINER: scrollable, just without visible scrollbars. This
                        // wrapper always has scrollable overflow, because all four
                        // panels share one grid cell while the wrapper's height tracks
                        // only the ACTIVE one — so the taller inactive panels (plus
                        // their 10px rest-offset `translateY`) push `scrollHeight`
                        // past `clientHeight` permanently. Measured on step 2:
                        // clientHeight 279, scrollHeight 294.
                        //
                        // Nothing scrolls it deliberately, but the BROWSER does: on
                        // focus, it scrolls the focused element into view in its
                        // nearest scroll container. The volume select is the last
                        // field in the panel, so clicking it scrolled this wrapper to
                        // the bottom and every field jumped up by the overflow amount
                        // — and stayed there, since nothing resets scrollTop.
                        // Measured: `scrollTop` 0 -> 15, trigger top 815 -> 800.
                        //
                        // `overflow: clip` clips identically but is NOT a scroll
                        // container, so there is nothing for the browser to scroll.
                        // Re-measured with it: scrollTop stays 0, trigger stays at
                        // 815, delta 0.
                        //
                        // Kept behind `@supports` with `hidden` as the fallback
                        // because the two do not degrade alike: an unsupported `clip`
                        // is simply dropped, leaving `overflow: visible` and all four
                        // steps painted on top of each other — far worse than the
                        // shift being fixed. (`clip` is Chrome 90+/FF 81+/Safari 16+,
                        // so the fallback should never actually be used.)
                        //
                        // Losing the `hidden`-implied block formatting context costs
                        // nothing here: a grid container already establishes an
                        // independent formatting context on its own.
                        overflow: 'hidden',
                        '@supports (overflow: clip)': { overflow: 'clip' },
                        // Trimmed 32px -> 20px for the no-vertical-scroll-at-1280x680
                        // budget (working-conventions height budget, VERA-1095).
                        mt: '20px',
                        // Follow the ACTIVE panel's height, not the tallest one.
                        // Without this the viewport is as tall as the tallest step
                        // (a grid stack sizes to its tallest item, same as the old
                        // flex row did), which parked Continue 140px below Step 1's
                        // card. `activePanelHeight` is DERIVED (`panelHeights[
                        // stepIndex]`, see that state's own comment) so it already
                        // holds the CORRECT target the instant `stepIndex` changes —
                        // the earlier "dead space" bug was never caused by animating
                        // height, it was caused by animating TOWARD A WRONG VALUE
                        // (a stale/self-referential measurement). With the value
                        // itself fixed, height can safely share the cross-fade's
                        // own 200ms/`ease` — same duration, same easing, same
                        // trigger — so the box and its content move together
                        // instead of the box snapping ahead of a fading label.
                        //
                        // THE PAYMENT STEP USED TO OPT OUT OF THIS, and no longer
                        // does. That opt-out was written for EMBEDDED CHECKOUT,
                        // which mounted one large iframe at 0px and grew to
                        // ~1112px on Stripe's own schedule (measured: ~19s in the
                        // preview pane) — a height that was always stale at mount
                        // and, with `overflow: hidden` just above, could clip the
                        // pay button to a sliver in an environment where
                        // ResizeObserver never fires (which the CCD preview pane
                        // is: probed directly, 0 callbacks, initial included).
                        // Leaving it on `auto` cost nothing then, because embedded
                        // Checkout was the TALLEST panel anyway and the step had no
                        // Continue of ours to strand.
                        //
                        // Both halves of that stopped being true. Our own
                        // PaymentElement form is short (~430px measured) rather than
                        // the tallest panel, so `auto` resolves to the PLAN
                        // MATRIX's ~1035px and leaves ~900px of dead space; and the
                        // step now HAS a CTA of ours, which that dead space pushed
                        // below the fold. Measured before this change: the "Pay $99"
                        // button sat at y=1335 in a 1304px viewport.
                        //
                        // Re-joining is safe because the fallback is `auto`, not a
                        // stale number: `activePanelHeight` is `panelHeights[
                        // stepIndex]`, which is null until the observer has reported
                        // at least once. So in the preview pane (no observer) this
                        // degrades to exactly the previous behaviour — tall, never
                        // clipped — while a real browser tracks the panel as Stripe's
                        // per-field iframes mount and grow. The failure mode the
                        // opt-out guarded against required a stale SMALL value, and
                        // there is no path that produces one.
                        height: activePanelHeight ? `${activePanelHeight}px` : undefined,
                        transition: prefersReducedMotion ? 'none' : 'height 0.2s ease',
                      }}
                    >
                      {stepKeys.map((key, panelIndex) => {
                        const isActive = panelIndex === stepIndex
                        // Resting position for an INACTIVE panel: above if it's a
                        // step the visitor has already passed, below if it's a
                        // step still ahead — see the block comment above for why.
                        const restOffset = panelIndex < stepIndex ? -10 : panelIndex > stepIndex ? 10 : 0
                        return (
                          <Box
                            key={key}
                            ref={(node: HTMLDivElement | null) => {
                              panelRefs.current[panelIndex] = node
                            }}
                            aria-hidden={!isActive}
                            sx={{
                              gridColumn: 1,
                              gridRow: 1,
                              width: '100%',
                              // Sized by CONTENT, not stretched to the pinned row —
                              // without this the panel's measured height IS the height
                              // the observer set, so any content growth (a field error
                              // appearing) is invisible to the observer and clipped
                              // permanently. Full reasoning on the twin in
                              // `SignInPage`'s `stepPanelSx`.
                              alignSelf: 'start',
                              opacity: isActive ? 1 : 0,
                              transform: `translateY(${isActive ? 0 : restOffset}px)`,
                              // Inactive panels never receive clicks/taps — they're
                              // invisible, and without this a mid-fade panel could
                              // still intercept a pointer event meant for the one
                              // fading in on top of it.
                              pointerEvents: isActive ? 'auto' : 'none',
                              /*
                                VRG-595 — AND THEY ARE NOT TAB-REACHABLE EITHER.

                                `opacity: 0` + `pointer-events: none` + `aria-hidden`
                                stops the mouse and the screen reader; it does NOTHING
                                to the TAB ORDER. Measured before this: on the gate,
                                every input in the invisible identity and company
                                panels reported `tabIndex: 0`, so tabbing off the email
                                field walked a keyboard user into a first-name box, a
                                password box and a subdomain box they could not see and
                                could type into. Pre-existing (it was already true
                                between identity and company), but the gate is what
                                makes it matter: the whole point of the gate is that an
                                unchecked visitor is not asked to fill in a form, and a
                                form reachable by Tab is still a form.

                                `visibility: hidden` is what removes an element from
                                the tab order while KEEPING its layout box, which is
                                load-bearing here — the panel-height ResizeObserver
                                measures every panel, active or not, and `display: none`
                                would collapse those measurements to zero and break the
                                animated height entirely.

                                The 0.2s DELAY on the way OUT is the whole trick.
                                `visibility` is not interpolatable, so it flips
                                discretely; delaying that flip until the opacity
                                transition has finished lets the outgoing panel fade
                                normally instead of vanishing on frame one. Coming IN it
                                flips immediately (0s), or the incoming panel would fade
                                up from nothing while still hidden.
                              */
                              visibility: isActive ? 'visible' : 'hidden',
                              transition: [
                                'opacity 0.2s ease',
                                ...(prefersReducedMotion ? [] : ['transform 0.2s ease']),
                                `visibility 0s linear ${isActive ? '0s' : '0.2s'}`,
                              ].join(', '),
                            }}
                          >
                            {/* VRG-595 — THE ELIGIBILITY GATE. One field, and it is
                                the ONLY email input on the page: the identity step
                                below shows the confirmed address read-only rather
                                than registering a second `email` field, which would
                                put two RHF registrations on one name and two DOM
                                inputs on one value. */}
                            {key === 'email' && (
                              <Box sx={{ maxWidth: FORM_WIDTH, mx: 'auto', width: '100%' }}>
                                {/*
                                  VRG-595 — THE REQUEST-LEVEL ERROR SITS ABOVE THE
                                  FIELD ON THIS STEP, not below it in the footer where
                                  every other step keeps it.

                                  Two kinds of error, and the split is unchanged: a
                                  FIELD error ("Enter a valid email address") is RHF's
                                  and still renders as helper text under the input at
                                  `FieldLabel`'s canonical 8px (§31 — untouched here).
                                  A REQUEST error (throttled, server down, eligibility
                                  call failed) is the `error` state, and `error` is only
                                  ever set from a catch, so it is request-level by
                                  construction — there is no filtering to do at the
                                  render site.

                                  Why it moves on THIS step specifically: the gate has
                                  exactly ONE input, and the thing the reader will do
                                  next is re-edit it. Above the field, the explanation
                                  is met before the control it is about, in visual AND
                                  in DOM order (so a screen reader reaches it first
                                  too). Left in the footer it also wedged itself between
                                  the field and the CTA that acts on it, splitting a
                                  pair that belongs together.

                                  It does NOT move on the other steps, and that is
                                  deliberate rather than an omission. Identity, company,
                                  plan and payment have many fields, so a submit error
                                  is not "about" any one of them — the footer's own
                                  comment makes that argument and it still holds there.
                                  The payment step in particular needs it beside the
                                  button, inside the sticky bar. One-field screen,
                                  different answer.
                                */}
                                {error && (
                                  <Alert
                                    severity="error"
                                    description={error}
                                    /*
                                      8px — TIGHTER than the form's own field-to-field
                                      rhythm (12px, the `spacing={3}` every step's Stack
                                      uses), on purpose. The alert and the field are one
                                      unit: the message explains the input directly
                                      beneath it, so it has to read as attached to it
                                      rather than as the previous item in a list.

                                      8px is not an invented value — it is `FieldLabel`'s
                                      canonical label-to-control gap (§31), which is the
                                      same relationship one step up: a thing that
                                      describes the control, immediately above the
                                      control. 12px was tried first and sat at the field
                                      rhythm, so the pair did not group; 4px is the next
                                      step down and reads cramped against the alert's own
                                      12px internal padding.

                                      The field's gap DOWN to Continue stays 24px (the
                                      footer wrapper's `pt`), so the pair is separated
                                      from the button by 3x what separates it internally.
                                    */
                                    sx={{ mb: '8px' }}
                                  />
                                )}
                                {inviteResolving ? (
                                  /* §19: a skeleton at the field's own footprint —
                                     label bar + control — so the invited visitor's
                                     brief wait does not shift the layout when the
                                     step it resolves to takes its place. Never a
                                     spinner on a blank. */
                                  <Box>
                                    <Skeleton width={48} height={14} sx={{ mb: '8px' }} />
                                    <Skeleton height={49} />
                                  </Box>
                                ) : (
                                  <TextField
                                    {...methods}
                                    name="email"
                                    label="Email"
                                    placeholder="Enter your work email"
                                    rules={{
                                      required: 'Email is required',
                                      pattern: {
                                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                        message: 'Enter a valid email address',
                                      },
                                    }}
                                    // Controlled for its whole lifetime, so the
                                    // invite prefill (a `setValue` from an async
                                    // resolve) reaches the DOM without the field
                                    // ever flipping uncontrolled -> controlled.
                                    value={emailValue}
                                    onValueChange={onEmailValueChange}
                                  />
                                )}
                              </Box>
                            )}

                            {key === 'identity' && (
                              <Box sx={{ maxWidth: FORM_WIDTH, mx: 'auto', width: '100%' }}>
                                {/* NO CARD (user call, 2026-08-25). Steps 1 and 2
                                    put their fields straight on the ambient
                                    shell, the way sign-in already does — which
                                    is what makes the cross-fade between the two
                                    pages a fade rather than a structural pop.

                                    The card was doing no grouping work: the
                                    stepper shows ONE group at a time, so there
                                    was never a second group to separate from.
                                    Its title duplicated the stepper's own label
                                    for the step, so that goes with it.

                                    Safe now in a way it was not an hour ago: the
                                    card's one technical job was giving the
                                    controls a constant, opaque backdrop on an
                                    animated gradient. The fields carry their own
                                    opaque fill in light mode now, so they no
                                    longer need a surface underneath to supply
                                    it. */}
                                <Box>
                                  <Stack spacing={3}>
                                    <Stack direction="row" spacing={3}>
                                      <TextField
                                        {...methods}
                                        name="firstName"
                                        label="First name"
                                        placeholder="Enter your first name"
                                        rules={{ required: 'First name is required' }}
                                        onValueChange={() => revalidateIfErrored('firstName')}
                                      />
                                      <TextField
                                        {...methods}
                                        name="lastName"
                                        label="Last name"
                                        helperText="optional"
                                        placeholder="Enter your last name"
                                      />
                                    </Stack>
                                    {/*
                                      VRG-595 — THE CONFIRMED ADDRESS, not a second
                                      email field.

                                      This was a registered `TextField name="email"`
                                      until the eligibility gate moved the question one
                                      screen earlier. Leaving it here would have meant
                                      two `register('email')` calls and two DOM inputs
                                      bound to one value, permanently mounted side by
                                      side — RHF keeps only the LAST registered ref, so
                                      a blur on one would re-read its value out of the
                                      other. That is a subtle, load-order-dependent
                                      coupling in the one file whose header comment is a
                                      monument to exactly this class of bug, and it buys
                                      nothing: the visitor has already answered.

                                      Asking twice on consecutive screens is also just
                                      worse. What belongs here is a confirmation of who
                                      is signing up, which is what a read-only field is.

                                      `@repo/ui`'s `Input` rather than the platform
                                      `TextField`, because `TextField` exists to wire a
                                      field to react-hook-form and there is nothing to
                                      wire — no name, no rules, no ref. `Input` is the
                                      layer-1 primitive underneath it (§47) and owns the
                                      label, the 8px label gap and the field chrome
                                      itself (§31), so this is a real field visually
                                      while registering nothing.

                                      `readOnly`, never `disabled`: readOnly keeps full
                                      contrast and stays reachable by keyboard, where
                                      disabled fades to 0.4 and is skipped — and this
                                      value is the one thing on the step a visitor might
                                      want to select and copy.
                                    */}
                                    <Box>
                                      <Input label="Email" readOnly value={emailValue} />
                                      <Typography
                                        variant="body3"
                                        color="text.secondary"
                                        mt="12px"
                                        sx={{ lineHeight: 1.5 }}
                                      >
                                        {inviteEmail ? (
                                          <>
                                            This is the email your invite was sent to.
                                            {/* VRG-595: "Contact us" was plain text under a
                                                field the reader has just discovered they
                                                cannot edit, which is the moment they most
                                                need somewhere to go. The address is the link
                                                text so it survives a `mailto:` that does
                                                nothing. */}
                                            {' '}Need a different one? Email{' '}
                                            <AuthInlineLink href={SUPPORT_MAILTO}>
                                              {SUPPORT_EMAIL}
                                            </AuthInlineLink>.
                                          </>
                                        ) : (
                                          <>
                                            {/* The non-invited visitor typed this one
                                                screen ago, so the way to change it is to
                                                go back to where they typed it. An inline
                                                link rather than a button: Back is already
                                                the primary way out of this step and a
                                                second control of equal weight would
                                                compete with it. */}
                                            We checked this address against the invite list.{' '}
                                            <AuthInlineLink onClick={goBack}>
                                              Use a different email
                                            </AuthInlineLink>.
                                          </>
                                        )}
                                      </Typography>
                                    </Box>
                                    <TextField
                                      {...methods}
                                      name="password"
                                      type="password"
                                      label="Password"
                                      placeholder="At least 8 characters"
                                      // VERA-1095: `validate: passwordMeetsPolicy`, NOT
                                      // `pattern: passwordRex` — root-caused live: the
                                      // checklist (PASSWORD_REQUIREMENTS) is five POSITIVE
                                      // assertions ("contains a lowercase letter" etc.), so
                                      // it can never detect a character that is merely NOT
                                      // PERMITTED, but `passwordRex`'s trailing character
                                      // class IS an allowlist over the whole string. A
                                      // password containing a space, an accented letter, a
                                      // curly quote or any non-ASCII character satisfied
                                      // every checklist item (all five ticks green, bar full
                                      // purple) while still failing the regex — an error RHF
                                      // considered entirely correct, which is exactly why it
                                      // never cleared. `passwordMeetsPolicy` (constants/
                                      // passwordPolicy.ts) IS `PASSWORD_REQUIREMENTS.every(
                                      // ...)` — the checklist itself — so the gate and the
                                      // meter share one source and cannot disagree, by
                                      // construction rather than by a comment asking two
                                      // implementations to be kept in sync. Deliberately no
                                      // character allowlist: NIST SP 800-63B says accept all
                                      // printing ASCII including space, plus Unicode, and an
                                      // allowlist only rejects password-manager-generated
                                      // passwords for no security gain — WorkOS is the real
                                      // password store and runs its own strength check.
                                      rules={{
                                        required: 'Password is required',
                                        validate: (value?: string) =>
                                          passwordMeetsPolicy(value ?? '') || PASSWORD_POLICY_HELP_TEXT,
                                      }}
                                      onValueChange={() => revalidateIfErrored('password')}
                                    />
                                    {/* The same meter + requirement checklist the
                                        reset-password screen shows. Both screens set a
                                        password against the SAME policy
                                        (constants/passwordPolicy.ts), so showing the
                                        rules on one and not the other meant signup
                                        users only discovered the composition rule as a
                                        rejection AFTER pressing Continue. Rendered only
                                        once they start typing, so an untouched form
                                        stays as short as it looks now. */}
                                    {passwordValue.length > 0 && (
                                      <PasswordStrengthMeter password={passwordValue} />
                                    )}
                                  </Stack>
                                </Box>
                              </Box>
                            )}

                            {key === 'company' && (
                              <Box sx={{ maxWidth: FORM_WIDTH, mx: 'auto', width: '100%' }}>
                                <Box>
                                  {/* spacing={4} (16px), not the {3} (12px) used
                                      elsewhere on this page — item 2: the block
                                      read compressed with a helper line sitting
                                      right under one field and right above the
                                      next. Bumped here rather than in a shared
                                      spacing constant since this is the only
                                      field group on the page carrying helper
                                      text under a field. */}
                                  <Stack spacing={4}>
                                    <TextField
                                      {...methods}
                                      name="companyName"
                                      label="Company name"
                                      placeholder="Enter your company name"
                                      rules={{ required: 'Company name is required' }}
                                      onValueChange={(value: string) => {
                                        revalidateIfErrored('companyName')
                                        if (!subdomainEdited) setValue('subdomain', slugifySubdomain(value))
                                      }}
                                    />
                                    {/* VRG-113: the founder picks their own
                                        `<subdomain>.app.vera.inc` rather than
                                        discovering an auto-generated one later.
                                        Validation matches the API's exactly (shared
                                        helper) so we can't reject a name it would take.
                                        Labelled "Your unique address" (item 5) rather than
                                        "subdomain"/"username" — it's a URL the field's
                                        own suffix already shows, not a login handle. */}
                                    <Box>
                                      <TextField
                                        {...methods}
                                        name="subdomain"
                                        label="Your unique address"
                                        placeholder="your-company"
                                        rules={{ validate: validateSubdomain }}
                                        // Controlled, so the suggestion typed into it by
                                        // the company-name field is actually visible.
                                        value={subdomainValue}
                                        endAdornment={
                                          <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={1}
                                            sx={{ pr: 2 }}
                                          >
                                            {/* Item 1: the three availability states
                                                shown clearly, right next to the
                                                suffix — not just the line below. */}
                                            {subdomainAvailability.state === 'checking' && (
                                              <CircularProgress
                                                size={14}
                                                thickness={5}
                                                sx={{ color: theme.palette.text.secondary }}
                                              />
                                            )}
                                            {subdomainAvailability.state === 'available' && (
                                              <CheckIcon
                                                size={16}
                                                strokeWidth={2}
                                                color={theme.palette.success.main}
                                              />
                                            )}
                                            {subdomainAvailability.state === 'unavailable' && (
                                              <XIcon
                                                size={16}
                                                strokeWidth={2}
                                                color={theme.palette.error.main}
                                              />
                                            )}
                                            <Typography
                                              variant="body3"
                                              color="text.secondary"
                                              sx={{ whiteSpace: 'nowrap' }}
                                            >
                                              {getDomainSuffix()}
                                            </Typography>
                                          </Stack>
                                        }
                                        // Latch only — normalising case here would be
                                        // undone immediately by RHF's onChange; case is
                                        // handled by a case-insensitive rule and
                                        // normalised once, at submit.
                                        onValueChange={() => {
                                          revalidateIfErrored('subdomain')
                                          setSubdomainEdited(true)
                                          }}
                                      />
                                      {/* Item 2: mt bumped 8px -> 12px (the only
                                          remaining field-description helper on this
                                          page — see item 4's removal below — so the
                                          spacing fix lands in this one place). Item 3:
                                          the static copy is one short sentence; the
                                          "this is your address" framing was dropped as
                                          self-evident from the visible suffix. This
                                          same line carries the live availability
                                          verdict once one exists, rather than a
                                          separate row competing for space. */}
                                      {subdomainAvailability.state === 'checking' && (
                                        <Typography
                                          variant="body3"
                                          color="text.secondary"
                                          mt="12px"
                                          sx={{ lineHeight: 1.5 }}
                                        >
                                          Checking availability…
                                        </Typography>
                                      )}
                                      {subdomainAvailability.state === 'available' && (
                                        <Typography
                                          variant="body3"
                                          mt="12px"
                                          sx={{ lineHeight: 1.5, color: theme.palette.success.main }}
                                        >
                                          {subdomainAvailability.subdomain} is available
                                        </Typography>
                                      )}
                                      {subdomainAvailability.state === 'unavailable' && (
                                        <Typography
                                          variant="body3"
                                          mt="12px"
                                          sx={{ lineHeight: 1.5, color: theme.palette.error.main }}
                                        >
                                          {subdomainAvailability.subdomain} isn't available. Try another.
                                        </Typography>
                                      )}
                                      {subdomainAvailability.state === 'idle' && (
                                        <Typography
                                          variant="body3"
                                          color="text.secondary"
                                          mt="12px"
                                          sx={{ lineHeight: 1.5 }}
                                        >
                                          You can change this later in Settings.
                                        </Typography>
                                      )}
                                    </Box>
                                    {/* Item 4: the "just an estimate" helper is removed
                                        entirely — the question itself ("About how many
                                        units do you produce a year?") already reads as
                                        an estimate. No description left under this
                                        field, so no wrapping Box either. "Units", not
                                        "products" or "items" — "product" is already a
                                        Vera domain term for a catalogue record, so a
                                        brand with 50 SKUs and 500,000 units of annual
                                        output would answer "50" and be tier-sized as
                                        Starter instead of Enterprise. */}
                                    <SelectField
                                      label="About how many units do you produce a year?"
                                      options={PRODUCTION_VOLUME_OPTIONS}
                                      value={productionVolume}
                                      onChange={(value) => setProductionVolume(value as string)}
                                      placeholder="Select a range"
                                    />
                                  </Stack>
                                </Box>
                              </Box>
                            )}

                            {key === 'plan' && (
                              <Box sx={{ maxWidth: WIDE_WIDTH, mx: 'auto', width: '100%' }}>
                                {/* VRG-383: the SAME `PlanComparisonMatrix` the
                                    authenticated billing plan-change page renders,
                                    composed the same way — plan "cards" folded into
                                    the column headers, the Monthly/Annual control in
                                    the label column's header cell, and the Enterprise
                                    card immediately beneath. This page used to carry
                                    a near-copy of all of it (cards above a separate,
                                    collapsed matrix), with its own row data and its
                                    own tier-key derivation. */}
                                <Stack spacing={0}>
                                  {plansLoading && <PlanMatrixSkeleton columns={3} />}

                                  {plansError && (
                                    <Alert
                                      severity="error"
                                      description="Couldn't load plans. Please refresh and try again."
                                    />
                                  )}

                                  {!plansLoading && !plansError && filteredPlans.length === 0 && (
                                    <Alert
                                      severity="warning"
                                      /*
                                        VRG-595: the THIRD plain-text "contact support" on
                                        this page, and the same fix as the other two (see
                                        the eligibility-gate Alert and the invited-email
                                        helper above).

                                        This one is arguably the worst of the three: the
                                        reader has already been accepted, already filled in
                                        their details, and has hit a dead end with no
                                        action available on the screen at all. There is no
                                        "try again" that helps — the plan list is empty
                                        server-side — so the address IS the only next step,
                                        and telling them a route exists without giving it
                                        leaves them stuck mid-signup with no account yet
                                        and therefore no in-app support intake either.

                                        The link TEXT is the address itself, per
                                        `constants/support`: `mailto:` does nothing on
                                        webmail with no registered mail client, and when it
                                        does nothing the words still have to BE the answer.
                                        Inline, not a button — there is no competing
                                        primary action here, but a button would read as
                                        "this fixes it", and emailing support does not
                                        unblock the step in this session.
                                      */
                                      description={
                                        <>
                                          No plans are available right now. Email{' '}
                                          <AuthInlineLink href={SUPPORT_MAILTO}>{SUPPORT_EMAIL}</AuthInlineLink>{' '}
                                          to finish setting up your account.
                                        </>
                                      }
                                    />
                                  )}

                                  {!plansLoading && filteredPlans.length > 0 && (
                                    <>
                                      <PlanComparisonMatrix
                                        plans={filteredPlans}
                                        /* The SELECTED column, not a current one —
                                           nobody is subscribed yet. Same prop, same
                                           brand tint band, and it is the treatment
                                           §34 prescribes for a selectable card
                                           ("chosen"), so reusing it here is the
                                           consistent read rather than a third
                                           selected style invented for this page. */
                                        currentPlanTitle={selectedPlan?.title}
                                        planAction={planAction}
                                        /* Signup is a PICKER, so the whole column is
                                           a valid target: the action selects a plan,
                                           charges nothing, and is undone by picking
                                           another. Billing does not pass this — there
                                           the action navigates into checkout, and a
                                           column-sized hit area for a billing flow is
                                           a liability rather than a convenience. */
                                        columnClickable
                                        planSavingsLabel={planSavingsLabel}
                                        // Signup only — the billing page keeps its
                                        // fully-expanded matrix. See the prop's own
                                        // note for why the FIRST section stays open.
                                        collapseSectionsByDefault
                                        publicSurface
                                        centeredHeading
                                        // No `intervalControl` on signup. The
                                        // Monthly/Annual toggle now sits in the
                                        // step-indicator row ABOVE this card,
                                        // right-aligned to the card's own right
                                        // edge (see that row). The prop is
                                        // optional, so omitting it is the whole
                                        // change here — the billing plan-change
                                        // page still passes it and still renders
                                        // it in the matrix's corner cell, which
                                        // is a different branch entirely (it does
                                        // not set `centeredHeading`).
                                        // No `footer` on signup. It used to carry "Need an SLA,
                                        // data residency or procurement? Talk to us about
                                        // Enterprise", which is the EnterpriseContactCard's job
                                        // — and that card renders IMMEDIATELY below, listing
                                        // "Uptime SLA and priority response times" and "Data
                                        // residency options" as two of its four bullets, with a
                                        // Contact sales button. So the line asked a question the
                                        // next 100px answered, and gave the same tier two
                                        // different entry points a few pixels apart.
                                        //
                                        // The billing plan-change page keeps its own footer
                                        // (BillingPlanChangePage.tsx) — this is deliberately
                                        // scoped to signup, like the centred heading above it.
                                      />
                                      {/* Adjacency is load-bearing: this card is what
                                          anchors "Everything in <top plan>, plus" to
                                          the column it extends. Nothing goes between
                                          the two. `mt: 8` comes from the card itself. */}
                                      <EnterpriseContactCard
                                        topPricedPlanTitle={
                                          filteredPlans[filteredPlans.length - 1]?.title
                                        }
                                        onContact={handleContactSales}
                                      />
                                    </>
                                  )}
                                </Stack>
                              </Box>
                            )}

                            {key === 'payment' && (
                              // FORM_WIDTH, matching steps 1 and 2 rather than
                              // the plan step's 1120: this is a single-column
                              // card-entry form and stretching it to the
                              // matrix's width would leave one narrow input
                              // floating in a very wide panel. It is also what
                              // makes the card fields the same width as the
                              // email and company-name fields the visitor just
                              // filled in, which is half of why the step reads
                              // as part of the same form.
                              //
                              // This panel is why `gridTemplateColumns:
                              // 'minmax(0, 1fr)'` on the track above is
                              // load-bearing and must not be relaxed. Every
                              // panel shares ONE grid cell, so a fourth one that
                              // sized to its own content would widen the column
                              // for all of them and push steps 1-2 off-centre —
                              // the exact regression that fix was written for
                              // when the plan step became the shared matrix.
                              <Box sx={{ maxWidth: FORM_WIDTH, mx: 'auto', width: '100%' }}>
                                <SignupPaymentStep
                                  clientSecret={clientSecret}
                                  // OUR summary header, from the plan already
                                  // selected on step 3 — never read back from
                                  // Stripe. See SignupPaymentStep's doc.
                                  plan={selectedPlan}
                                  // Step 1's name, prefilled into the billing
                                  // address so nobody types their own name
                                  // twice in one form. Last name is OPTIONAL on
                                  // step 1, so this is joined by filtering
                                  // rather than concatenated — `${first} ${last}`
                                  // yields a trailing space when there is no
                                  // last name, which Stripe would prefill as a
                                  // real (wrong) value. Still editable on the
                                  // step: the name on the card is allowed to
                                  // differ from the name on the account.
                                  defaultName={[firstNameValue, watch('lastName')]
                                    .map((part) => (part ?? '').trim())
                                    .filter(Boolean)
                                    .join(' ')}
                                  registerSubmit={registerPaymentSubmit}
                                  onError={setError}
                                  // A refusal clears the inline Alert as it
                                  // sets the panel — the panel states the same
                                  // failure more fully, and showing both would
                                  // say it twice in two different registers.
                                  onFailed={(message) => {
                                    setError(null)
                                    setPaymentFailure(message)
                                  }}
                                  failure={paymentFailure}
                                  onRetry={remintPayment}
                                  retrying={reminting}
                                  onSucceeded={onPaymentSucceeded}
                                  onSubmittingChange={setPaying}
                                />
                              </Box>
                            )}
                          </Box>
                        )
                      })}
                    </Box>

                    {/* Trimmed 32px -> 20px, same no-vertical-scroll budget as the
                        track's own `mt` above — this is the card-to-button gap
                        Spencer flagged as inconsistent; see the track wrapper's
                        `alignItems: 'start'` fix for the actual cause (a stuck,
                        self-referential height, not this constant). */}
                    {/* VERA-1095: the submit-level error sits OUTSIDE the card, and
                        that is deliberate. There are two kinds of error on this page
                        and they belong in different places:

                        - FIELD errors ("Email is required") render inside the card,
                          under the field they are about. They already do.
                        - SUBMIT errors ("Something went wrong") are about the ACTION,
                          not any one field, so they belong with the button that
                          triggered them. Moving one inside the card would put it
                          beside fields that are all individually fine, and push the
                          CTA further from the thing that actually failed.

                        The gaps are now SYMMETRIC (12px / 12px, the §29 value). They
                        were 20px above and 8px below — the `pt` here plus a
                        `spacing={2}` Stack — so a full-width boxed Alert sat 2.5x
                        closer to the button than to the card and read as though it
                        had slipped out of position rather than been placed. */}
                    {/* STICKY on the plan step only.
                        Steps 1 and 2 are short forms whose CTA is already on
                        screen, so pinning it there would float a bar over
                        nothing. The plan step is the one that needs it: the
                        matrix runs well past a screen, and a visitor picks a
                        plan at the TOP of it, then had to scroll past every
                        comparison row — and the Enterprise card — to reach the
                        button that acts on the choice they already made.

                        `position: sticky` inside the page's own scroll
                        container (this page is `position: fixed; inset: 0` with
                        an inner `overflowY: auto`, so `fixed` would escape it
                        and pin to the viewport instead). `bottom: 0` with the
                        page's own background behind it, so rows scrolling
                        underneath do not show through the button.

                        zIndex 2 clears the matrix's sticky column headers. */}
                    <Box
                      sx={{
                        maxWidth: FORM_WIDTH,
                        mx: 'auto',
                        width: '100%',
                        // 24px from the card. Was 12px, which read as the card
                        // and the button belonging to one block.
                        //
                        // Note this makes the ERROR case 24 above / 12 below
                        // rather than the symmetric 12/12 set earlier, and that
                        // is the right asymmetry rather than a regression: the
                        // alert explains why the SUBMIT failed, so grouping it
                        // with the button and separating both from the card is
                        // what the spacing should say.
                        pt: '24px',
                        ...(currentStepKey === 'plan' && {
                          position: 'sticky',
                          bottom: 0,
                          zIndex: 2,
                          // Breathing room above, so the pinned button is not
                          // touching the row it happens to be covering.
                          pt: '24px',
                          pb: '24px',
                          // NO background on this wrapper. It used to paint
                          // `background.default` across the full FORM_WIDTH,
                          // which read as an opaque BAR with hard edges sitting
                          // over the matrix — and because the wrapper is wider
                          // than the button, the band extended past it on both
                          // sides. Only the BUTTON should look pinned.
                          //
                          // No fill at all. The gradient that replaced the solid
                          // bar still read as banding -- a lightening above the
                          // button and a dark block below it, because the
                          // wrapper is taller and wider than the button itself.
                          // The button is opaque, so it hides what passes behind
                          // it without help; anything visible in the gaps is just
                          // the page, which is what "floating over content"
                          // should look like.
                        }),
                      }}
                    >
                      <Stack spacing={3}>
                        {/* The error Alert stays on ALL steps including payment,
                            and on the payment step it now carries MORE than it
                            used to. Under embedded Checkout, card errors were
                            reported inside Stripe's iframe and only our own
                            errors (a failed mint, a lost network) surfaced
                            here. With Elements, `confirmPayment` returns the
                            card error to us, so a declined card and a failed
                            request appear in the same place, immediately above
                            the button that triggered them. */}
                        {/* OPAQUE wrapper. This Alert sits in the sticky footer,
                            whose own wrapper deliberately has no background (a fill
                            there read as a hard-edged bar). The Alert's surface is a
                            low-alpha error tint, so with nothing opaque behind it the
                            Enterprise card's text showed through the message — two
                            paragraphs interleaved.

                            Only the ALERT needs to occlude, and only while there is
                            an error, which is why this is here rather than back on
                            the wrapper. */}
                        {/* VRG-595: NOT on the gate — it renders its own copy of
                            this above the email field (see that panel's comment for
                            why a one-field screen answers differently). Rendering
                            both would show the same message twice; rendering only
                            here would put it between the field and its CTA. */}
                        {/* Brand sign-up Terms acceptance: founder path, plan step only,
                            directly above the CTA that creates the account, so the
                            consent sits beside the act it covers. The link opens the
                            published Terms in a new tab: navigating away in place
                            would throw away four steps of form. */}
                        {currentStepKey === 'plan' && !pendingCompany && (
                          <Stack direction="row" spacing={2} alignItems="flex-start">
                            <Checkbox
                              checked={acceptedTerms}
                              onChange={(_, next) => {
                                setAcceptedTerms(next)
                                setError(null)
                              }}
                              sx={{ p: 0, mt: '2px', '&.Mui-checked': { color: theme.palette.primary.main } }}
                              inputProps={{ 'aria-label': 'I accept the Terms of Service' }}
                            />
                            <Typography variant="body3" sx={{ color: 'text.secondary', fontSize: '15px', lineHeight: 1.5 }}>
                              I accept the{' '}
                              <Link
                                href={TERMS_OF_SERVICE_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                color="inherit"
                                sx={{ fontWeight: 500 }}
                              >
                                Terms of Service
                              </Link>
                              .
                            </Typography>
                          </Stack>
                        )}
                        {error && currentStepKey !== 'email' && (
                          <Box sx={{ backgroundColor: 'background.default', borderRadius: '8px' }}>
                            <Alert severity="error" description={error} />
                          </Box>
                        )}
                        {showContinue && (
                          <AuthPrimaryButton
                            fullWidth
                            loading={continueLoading}
                            disabled={continueDisabled}
                            onClick={onContinueClick}
                            sx={pulseSx}
                          >
                            {continueLabel}
                          </AuthPrimaryButton>
                        )}

                        {/* The annual nudge, as a LINK rather than a modal.
                            A "are you sure you don't want annual?" dialog was
                            considered and rejected: §43 earns a confirmation gate
                            with a downstream or irreversible consequence, and
                            picking monthly is neither (billing can switch it, and
                            the plan step says so). It would also fire on every
                            monthly checkout, which is the frequent-action case that
                            causes modal fatigue, and "are you sure you don't want to
                            pay us more" reads as a dark pattern even when the offer
                            is genuinely better value.

                            It is an ACTION, not a statement. A line that merely says
                            annual is cheaper leaves the visitor to find their own way
                            back two steps; this takes them there.

                            It ROUTES BACK to the plan step rather than flipping the
                            interval in place, and that is a correctness requirement,
                            not a UX preference. `clientSecret` is minted by
                            `onSubmitCheckout` on the way OUT of the plan step, not by
                            an effect watching the price — so flipping here would leave
                            a PaymentIntent bound to the MONTHLY price under a button
                            claiming annual, i.e. charging the wrong amount. `goBack`
                            already discards the intent on leaving this step, so
                            routing through it re-mints correctly on the way forward,
                            and the visitor sees the annual matrix and re-confirms. */}
                        {currentStepKey === 'payment' && !showAnnual && annualSavingLabel && (
                          <Box sx={{ textAlign: 'center', mt: 3 }}>
                            <AuthInlineLink
                              onClick={() => {
                                handleIntervalChange(true)
                                goBack()
                              }}
                            >
                              {`${annualSavingLabel} a year with an annual plan`}
                            </AuthInlineLink>
                          </Box>
                        )}

                        {/* Secondary to the primary action, so it sits BELOW it.
                            Above the title it competed with "Create your Vera
                            account" for the first thing read on the page; the way
                            OUT of a flow belongs after the way THROUGH it.
                            Omitted on the success screen, which already has its
                            own "Go to sign in" primary CTA.

                            Item 6: shown on Step 1 ONLY — by Step 2 the visitor
                            has committed to signing up, and this escape hatch
                            competes with the flow. The step header's own "Back"
                            control (stepIndex > 0) already covers going
                            backwards from there.

                            VRG-595: also on the GATE, which is now the page's
                            landing screen for anyone without an invite link.
                            "Item 6" above is an argument about the FIRST screen
                            a visitor lands on, and that screen moved — leaving
                            this on identity alone would have taken the escape
                            hatch off the arrival page and put it one step in,
                            which is the opposite of what it is for. */}
                        {(currentStepKey === 'email' || currentStepKey === 'identity') && (
                          <Typography
                            variant="body3"
                            // 15px, matching sign-in's "Don't have an account?" line
                            // (Spencer, 2026-09-23: the small auth links were "too
                            // small, kind of hard to read").
                            /* `authPx(15)`, NOT a bare '15px'. Matching sign-in's
                               "Don't have an account?" means matching its RENDERED
                               size, and that line is `authPx(15)` — 12px on desktop.
                               A literal 15px here stayed 15px while the rest of the
                               card scaled, which left this sentence the one thing on
                               the page that did not match its twin. */
                            sx={{ color: 'text.secondary', textAlign: 'center', fontSize: authPx(15) }}
                          >
                            Already have an account?{' '}
                            <AuthInlineLink onClick={() => leaveToSignIn(ROUTES.SIGN_IN)}>
                              Sign in
                            </AuthInlineLink>
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                )}
              </Box>
            </Stack>
          </Box>
        </Stack>
        </Box>
      </Box>
  )
}

/**
 * VRG-595 — the invitee's first name, capped and normalised, or null.
 *
 * ONE function so "absent", `''` and `'   '` cannot be treated differently by
 * different call sites. All three mean the same thing here and all three take
 * the un-named branch: an invitee allowlisted directly by staff has no waitlist
 * row at all, so a missing name is the ORDINARY case, not a failure. The
 * greeting must degrade to the impersonal title with no gap, no stray comma and
 * no "there".
 *
 * ⚠️ THE INPUT IS UNVERIFIED FREE TEXT typed into a public marketing form.
 * Escaping is handled by rendering it as a React child — never
 * `dangerouslySetInnerHTML` — and this is the LENGTH half: 24 characters is
 * what "You're in, {name}" tolerates on one line at FORM_WIDTH (640px) in the
 * page title's 24px/600, and a pathological 400-character value would otherwise
 * wrap the banner to a paragraph or force the layout wide.
 *
 * FIRST name only, by construction — the caller passes `result.firstName`. A
 * full name reads like a form letter, and "van der Berg" style surnames are
 * exactly what pushes the line over. The ellipsis is a real character rather
 * than CSS truncation because the value is interpolated mid-sentence, where
 * `text-overflow` has nothing to clip against.
 */
const INVITE_NAME_MAX = 24

const normaliseInviteName = (raw: string | null | undefined): string | null => {
  const trimmed = (raw ?? '').trim()
  if (!trimmed) return null
  return trimmed.length > INVITE_NAME_MAX
    ? `${trimmed.slice(0, INVITE_NAME_MAX).trimEnd()}…`
    : trimmed
}

const STEP_TITLE: Record<StepKey, string> = {
  /*
    The gate names the DESTINATION, and never promises an account. "Create your
    Vera account" above it would be a promise this screen cannot keep for an
    uninvited visitor, which is the whole defect VRG-595 fixes.

    ⚠️ DO NOT reintroduce "Early access", "Check your invite", or any wording
    implying the product is young or unfinished. Both were rejected by the user
    for one reason: this product sells provenance and trust, and "early access"
    is standard SaaS shorthand for "not ready yet" — a positioning admission
    Vera must not make on the first screen a prospect sees.
  */
  email: 'Access Vera',
  identity: 'Create your Vera account',
  company: 'Tell us about your company',
  plan: 'Choose your plan',
  payment: 'Add your payment details',
}

const STEP_DESCRIPTION: Record<StepKey, string> = {
  /*
    States the constraint FIRST, so the possible refusal is framed before it
    happens rather than after three fields of work. §15: two sentences, no
    em-dash.

    ⚠️ "right now" was DELETED and must not come back, nor may "for now", "at
    the moment", "currently", "yet", "still", or a beta/waitlist framing. A
    time qualifier implies a phase that ends, which is the same "we are not
    ready" admission as "early access", only slower. "Vera is invite-only"
    reads as deliberate policy.

    "the email your invite was sent to" is load-bearing, not decoration: it
    tells someone with several addresses WHICH one to use, which is the
    likeliest cause of a false refusal on this screen.
  */
  email: 'Vera is invite-only. Enter the email your invite was sent to.',
  identity: 'One quick setup to get your team started on Vera.',
  company: 'A few details so we can set up your workspace.',
  plan: 'Pick the plan that fits — you can change this anytime.',
  // §15: no em-dashes in UI copy. "Your account is created once payment goes
  // through" is not filler — it is the one thing about this step a visitor
  // cannot infer, and stating it is what makes the deferred-provisioning
  // ordering (VRG-289) read as deliberate rather than as a slow signup.
  //
  // The second half is where the aspirational note lives. It sits HERE and not on
  // the button deliberately: the button that charges a card should name the amount
  // and the cadence, not a slogan, and this line costs nothing to warm up. It also
  // answers the question the previous version left open — the visitor is told what
  // happens on success, but not what they get to DO next.
  payment: 'Last step. Your account is created as soon as payment goes through.',
}

/**
 * Adapts the PUBLIC signup catalog row to the shape `PlanComparisonMatrix`
 * renders a column from (VRG-383). Two things happen here and both matter:
 *
 * 1. `tierKey` -> `tier_key`. The matrix keys EVERY per-tier row on it, and the
 *    signup payload now carries it (see `SignupPlan.tierKey`). Without the
 *    field the matrix falls back to deriving a key from the display title and
 *    renders dashes the moment that derivation misses.
 * 2. The "Vera " prefix is stripped from the title. The authenticated catalog
 *    strips it at its own boundary (`subscriptionApi` -> `stripPlanTitle`); the
 *    public one does not, so without this the columns would read "Vera Starter"
 *    while the rest of the app says "Starter".
 *
 * `features` is deliberately dropped: the matrix renders the curated
 * `constants/planComparison` rows, not Stripe's free-text `marketing_features`
 * (see that file's own doc comment for why).
 */
const toPlanColumn = (plan: SignupPlan): PlanColumnPlan => ({
  // No internal product id on the public payload — the price id is the stable
  // per-column key, and it is also what the checkout call needs.
  id: plan.priceId,
  title: stripVeraPlanPrefix(plan.title) ?? plan.title,
  description: plan.description,
  amount: plan.amount,
  interval: plan.interval,
  included_activations: plan.includedActivations,
  tier_key: plan.tierKey,
})

const matchesPlanInterval = (plan: PlanColumnPlan, annual: boolean): boolean => {
  const interval = (plan.interval || '').toString().toLowerCase()
  if (annual) {
    return interval === 'annual' || interval === 'annually' || interval === 'year'
  }
  return interval === 'month' || interval === 'monthly'
}

/**
 * The full both-interval catalog narrowed to one interval, cheapest first —
 * the column order the matrix renders. Used for the empty-state check and the
 * preselect too, so the page and the matrix never disagree on what is visible.
 */
const filterPlansByInterval = (
  plans: PlanColumnPlan[],
  annual: boolean,
): PlanColumnPlan[] =>
  plans
    .filter((plan) => matchesPlanInterval(plan, annual))
    .sort((a, b) => a.amount - b.amount)

/**
 * VERA-1095 — resolves a production-volume bucket's TIER to a real plan in the
 * live catalog, by bare tier key ('starter' | 'individual' | 'business' |
 * 'scale').
 *
 * The bucket already carries its tier (see signupFormOptions.ts, where the
 * boundaries come from VRG-305's acquisition tiers). This only has to find the
 * matching plan, which means going through the tier key — the catalog's title
 * is "Vera Business" while every tier map in the app is keyed `business`, and
 * comparing raw titles is what previously made the whole comparison matrix
 * render as em-dashes.
 *
 * VRG-383: that key now comes from the catalog's own `tier_key` column
 * (`resolvePlanTierKey`), not from stripping the display title. The title
 * derivation survives only as that function's fallback.
 *
 * An earlier version computed the tier from `includedActivations` instead,
 * treating N units/yr as needing N/12 activations/mo. That was the wrong
 * bridge: included activations are a base allotment covering the subscription
 * cost, not a production quota — everything beyond is billed on the graduated
 * $25 -> $5 scale. It also made the top bucket "more than 500 a year", i.e.
 * the SMALLEST acquisition tier.
 *
 * Falls back to the priciest plan rather than leaving nothing selected if the
 * tier has no plan in the catalog.
 *
 * SOFT DEFAULT ONLY, carrying the caveat VRG-305's demos.ts states for its own
 * headcount proxy: self-reported volume has an obvious incentive to
 * under-report, which is tolerable here specifically BECAUSE this only ever
 * preselects a starting point — a manual click on any plan card always wins
 * (`planManuallySelected`).
 */
function pickTierForVolume(allPlans: PlanColumnPlan[], bucketTier: string): string | null {
  const monthlyPlans = filterPlansByInterval(allPlans, false)
  if (monthlyPlans.length === 0) return null
  const match = monthlyPlans.find((plan) => resolvePlanTierKey(plan) === bucketTier)
  return resolvePlanTierKey(match ?? monthlyPlans[monthlyPlans.length - 1])
}
